import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Autocomplete,
  InputAdornment,
  CircularProgress,
  useMediaQuery,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowBack,
  ArrowForward,
  Save,
  Send,
  ReceiptLong,
  Person,
  Assignment,
  CheckCircle,
  Home as HomeIcon,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Importar configuración y hooks
import {
  DTE_TYPES,
  COUNTRIES,
  DOCUMENT_TYPES,
  DOCUMENT_FORMATTERS,
  DOCUMENT_MAX_LENGTHS,
  STEPS_CONFIG,
  getDTETypeConfig,
  getDocumentTypeConfig,
  getDocumentHelperText,
  isNRCRequired,
  getStepsForDTE,
  getInitialDocumentState,
  getReceptorRequiredFields,
  UNIDADES_MEDIDA,
  TIPOS_VENTA,
  TIPOS_ITEM,
  PLAZOS_CREDITO,
  METODOS_PAGO,
  FORMAS_PAGO,
  createNewItem,
  calculateRetentionAmounts,
} from './config/dteConfig';

import useDTEValidation from './hooks/useDTEValidation';
import ReceptorSearch from './steps/ReceptorSearch';
import EconomicActivitySelect from '../../common/EconomicActivitySelect';
import DistrictSelect from '../../common/DistrictSelect';
import { settingsService, authenticatedFetch, parseJsonResponse } from '../../../services/api';
import { getRequiredFields, validateDtePayload } from './utils/schemaValidator';
import { hasDocumentReference } from './config/dteConfig';
import StepDetailReferencia from './steps/StepDetailReferencia';
import { getReceptionSeal, normalizeMhObservations } from './utils/submissionResult';


const isEmptyish = (value) => value === '' || value === null || value === undefined;

// Quitar campos opcionales "vacíos" del JSON final (no enviar null ni "")
// Nota: esto NO es un "clean" genérico; aplica solo a paths opcionales que MH/AJV validan por tipo/length.
const stripEmptyOptionalFields = (dteJson) => {
  if (!dteJson || typeof dteJson !== 'object') return dteJson;

  const next = (typeof structuredClone === 'function')
    ? structuredClone(dteJson)
    : JSON.parse(JSON.stringify(dteJson));

  // identificacion (campos de contingencia opcionales)
  if (next.identificacion && typeof next.identificacion === 'object') {
    if (isEmptyish(next.identificacion.tipoContingencia)) next.identificacion.tipoContingencia = null;
    if (isEmptyish(next.identificacion.motivoContin)) next.identificacion.motivoContin = null;
  }

  const tipoDte = String(next.identificacion?.tipoDte || '');

  // El esquema CR v2 no define estos campos raíz: tampoco pueden enviarse en null.
  if (tipoDte === '07') {
    delete next.documentoRelacionado;
    delete next.otrosDocumentos;
    delete next.ventaTercero;
    delete next.extension;
  } else {
    if (isEmptyish(next.documentoRelacionado) || (Array.isArray(next.documentoRelacionado) && next.documentoRelacionado.length === 0)) next.documentoRelacionado = null;
    if (isEmptyish(next.otrosDocumentos) || (Array.isArray(next.otrosDocumentos) && next.otrosDocumentos.length === 0)) next.otrosDocumentos = null;
    if (isEmptyish(next.ventaTercero) || (typeof next.ventaTercero === 'object' && next.ventaTercero && Object.keys(next.ventaTercero).length === 0)) next.ventaTercero = null;
    if (isEmptyish(next.extension) || (typeof next.extension === 'object' && next.extension && Object.keys(next.extension).length === 0)) next.extension = null;
  }
  if (isEmptyish(next.apendice) || (Array.isArray(next.apendice) && next.apendice.length === 0)) next.apendice = null;

  // receptor: normalizar por tipoDte para evitar additionalProperties en schemas
  if (next.receptor && typeof next.receptor === 'object') {
    if (isEmptyish(next.receptor.telefono)) next.receptor.telefono = null;

    if (tipoDte === '01' || tipoDte === '04' || tipoDte === '11') {
      const {
        tipoDocumento,
        numDocumento,
        nrc,
        nombre,
        codActividad,
        descActividad,
        direccion,
        telefono,
        correo,
      } = next.receptor;
      next.receptor = {
        tipoDocumento,
        numDocumento,
        nrc,
        nombre,
        codActividad,
        descActividad,
        direccion,
        telefono,
        correo,
      };
    } else {
      if (isEmptyish(next.receptor.nombreComercial)) next.receptor.nombreComercial = next.receptor.nombre || 'N/A';
    }
  }

  // resumen.pagos: quitar campos opcionales vacíos
  if (next.resumen?.pagos && Array.isArray(next.resumen.pagos)) {
    next.resumen.pagos = next.resumen.pagos.map((p) => {
      if (!p || typeof p !== 'object') return p;
      const pago = { ...p };
      if (isEmptyish(pago.referencia)) pago.referencia = null;
      if (isEmptyish(pago.plazo)) pago.plazo = null;
      if (isEmptyish(pago.periodo)) pago.periodo = null;
      return pago;
    });
  }

  // cuerpoDocumento: quitar campos opcionales vacíos
  if (Array.isArray(next.cuerpoDocumento)) {
    next.cuerpoDocumento = next.cuerpoDocumento.map((item) => {
      if (!item || typeof item !== 'object') return item;
      const nextItem = { ...item };
      if (isEmptyish(nextItem.numeroDocumento)) nextItem.numeroDocumento = null;
      if (tipoDte === '07') {
        delete nextItem.codTributo;
      } else if (isEmptyish(nextItem.codTributo)) {
        nextItem.codTributo = null;
      }
      return nextItem;
    });
  }

  return next;
};

// ============================================
// FUNCIÓN PARA OBTENER DATOS DEL EMISOR
// ============================================
const getEmisorData = () => {
  try {
    const modules = localStorage.getItem('nx2026_modules');
    console.log('[DEBUG] nx2026_modules raw:', modules);
    if (modules) {
      const parsed = JSON.parse(modules);
      console.log('[DEBUG] nx2026_modules parsed:', parsed);
      if (parsed && parsed.length > 0 && parsed[0].company) {
        const company = parsed[0].company;
        console.log('[DEBUG] Company from localStorage:', company);
        // Mapear los datos del localStorage al formato esperado
        const mappedData = {
          id: company.id,
          nit: company.nit || '',
          nrc: company.nrc || '',
          legal_name: company.name || company.legal_name || '',
          commercial_name: company.commercial_name || '',
          economic_activity_code: company.economic_activity_code || '',
          economic_activity_desc: company.economic_activity_desc || '',
          main_departamento_code: company.main_departamento_code || '',
          main_municipio_code: company.main_municipio_code || '',
          main_address: company.main_address || '',
          bill_phone: company.bill_phone || company.phone || '',
          bill_email: company.bill_email || company.email || '',
          environment: company.environment || 'test'
        };
        console.log('[DEBUG] Mapped emisor data:', mappedData);
        return mappedData;
      }
    }
  } catch (e) {
    console.error('Error getting emisor data:', e);
  }
  return null;
};

// ============================================
// FUNCIÓN PARA GENERAR NÚMERO DE CONTROL
// Formato oficial MH: DTE-{tipoDte}-{codEstableMH}{codPuntoVenta}-{correlativo}
// - tipoDte: 01, 03, etc.
// - codEstableMH: 4 caracteres (código de establecimiento MH, ej: M001)
// - codPuntoVenta: 4 caracteres (letra + 3 dígitos, ej: P001)
// - correlativo: 15 dígitos
// Ejemplo: DTE-01-M001P001-000000000000001 (31 caracteres total)
// NO hay guión entre codEstableMH y codPuntoVenta
// ============================================
const generateNumeroControl = (tipoDte, codEstableMH, codPuntoVenta, correlativo = 1) => {
  // Establecimiento MH: 4 caracteres (usar código MH o valor por defecto)
  const estableMH = (codEstableMH && codEstableMH.length >= 4) 
    ? codEstableMH.substring(0, 4).toUpperCase() 
    : 'M001';
  
  // Punto de venta: formato letra + 3 dígitos (P001, P002, etc)
  // Debe ser exactamente 4 caracteres
  const puntoVenta = (codPuntoVenta && /^[A-Za-z]\d{3}$/.test(codPuntoVenta))
    ? codPuntoVenta.toUpperCase()
    : `P${String(codPuntoVenta || '001').padStart(3, '0')}`;
  
  // Combinarlos SIN guión para obtener 8 caracteres (según schema MH)
  const estableSinGuion = `${estableMH}${puntoVenta}`;  // 8 caracteres (4+4)
  
  // Correlativo: 15 dígitos (según schema MH: \d{15})
  const correlativoStr = String(correlativo).padStart(15, '0');
  
  // El formato correcto es: DTE-{tipoDte}-{codEstableMH}{codPuntoVenta}-{correlativo}
  // Ejemplo: DTE-01-CNSSP001-000000000000001 (31 caracteres)
  const numeroControl = `DTE-${tipoDte}-${estableSinGuion}-${correlativoStr}`;
  
  // Loguear para debugging
  console.log("numeroControl:", numeroControl);
  console.log("longitud total:", numeroControl.length);  // debe ser 31
  const parts = numeroControl.split('-');
  console.log("segmentos:", parts);  // ["DTE","01","CNSSP001","000000000000001"]
  console.log("  [0]:", parts[0], "- literal 'DTE'");
  console.log("  [1]:", parts[1], "- tipoDte");
  console.log("  [2]:", parts[2], "- codEstableMH+codPuntoVenta (8 caracteres)");
  console.log("  [3]:", parts[3], "- correlativo (15 dígitos)");
  
  return numeroControl;
};

const normalizePosValue = (value) => {
  const raw = String(value || '').trim().toUpperCase();
  if (!raw) return '';
  if (/^[A-Z]\d{3}$/.test(raw)) return raw;

  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';

  return `P${digits.padStart(3, '0').slice(-3)}`;
};

const parseInvoiceJsonData = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const mergeBackendDteIdentity = (localJson, persistedJson) => {
  if (!persistedJson) return localJson;

  const nextJson = {
    ...localJson,
    identificacion: {
      ...(localJson?.identificacion || {}),
    },
    emisor: {
      ...(localJson?.emisor || {}),
    },
  };

  if (persistedJson?.identificacion?.numeroControl) {
    nextJson.identificacion.numeroControl = persistedJson.identificacion.numeroControl;
  }

  const persistedEmisor = persistedJson?.emisor || {};
  nextJson.emisor.codEstable = persistedEmisor.codEstable || nextJson.emisor.codEstable;
  nextJson.emisor.codPuntoVenta = persistedEmisor.codPuntoVenta || nextJson.emisor.codPuntoVenta;

  return nextJson;
};

// ============================================
// FUNCIÓN PARA CONVERTIR NÚMEROS A LETRAS (Español de El Salvador)
// ============================================
const numberToWords = (num) => {
  if (num === null || num === undefined || isNaN(num)) return 'CERO DOLARES';
  
  const entero = Math.floor(num);
  const decimales = Math.round((num - entero) * 100);
  
  const unidades = ['', 'UN', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
  const especiales = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISÉIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
  const decenas = ['', 'DIEZ', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
  const centenas = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];
  
  const convertGroup = (n) => {
    if (n === 0) return '';
    if (n < 10) return unidades[n];
    if (n < 20) return especiales[n - 10];
    if (n < 100) {
      const dec = Math.floor(n / 10);
      const uni = n % 10;
      return decenas[dec] + (uni > 0 ? ' Y ' + unidades[uni] : '');
    }
    if (n < 1000) {
      const cent = Math.floor(n / 100);
      const rest = n % 100;
      if (n === 100) return 'CIEN';
      return centenas[cent] + (rest > 0 ? ' ' + convertGroup(rest) : '');
    }
    if (n < 1000000) {
      const mil = Math.floor(n / 1000);
      const rest = n % 1000;
      if (mil === 1) return 'MIL' + (rest > 0 ? ' ' + convertGroup(rest) : '');
      return convertGroup(mil) + ' MIL' + (rest > 0 ? ' ' + convertGroup(rest) : '');
    }
    if (n < 1000000000) {
      const millon = Math.floor(n / 1000000);
      const rest = n % 1000000;
      if (millon === 1) return 'UN MILLÓN' + (rest > 0 ? ' ' + convertGroup(rest) : '');
      return convertGroup(millon) + ' MILLONES' + (rest > 0 ? ' ' + convertGroup(rest) : '');
    }
    return n.toString();
  };
  
  const parteEntera = convertGroup(entero);
  
  // Formatear el resultado según el formato requerido por Hacienda
  if (decimales > 0) {
    return `${parteEntera} DOLARES CON ${decimales < 10 ? '0' : ''}${decimales} CENTAVOS`;
  } else {
    return `${parteEntera} DOLARES EXACTOS`;
  }
};

const getNowInElSalvador = () => {
  const parts = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'America/El_Salvador',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date());

  const byType = Object.fromEntries(parts.filter((p) => p.type !== 'literal').map((p) => [p.type, p.value]));
  return {
    fecEmi: `${byType.year}-${byType.month}-${byType.day}`,
    horEmi: `${byType.hour}:${byType.minute}:${byType.second}`,
  };
};

// ============================================
// FUNCIÓN PRINCIPAL: GENERAR JSON COMPLETO SEGÚN TIPO DTE
// ============================================
const buildCompleteDTEJson = (document, company, branches) => {
  const tipoDte = document.identificacion.tipoDte;
  const isNotaCreditoDebito = tipoDte === '05' || tipoDte === '06';
  const nowSv = getNowInElSalvador();
  const fecEmi = nowSv.fecEmi;
  const horEmi = nowSv.horEmi;
  
  // Obtener datos de la sucursal seleccionada
  const selectedBranch = branches.find(b => b.codigo_establecimiento === document.header?.sucursal);
  
  // Determinar el código de punto de venta (formato P001)
  const puntoVentaRaw = document.header?.puntoVenta || '001';
  const codPuntoVenta = puntoVentaRaw.match(/^[A-Za-z]\d{3}$/)
    ? puntoVentaRaw.toUpperCase()
    : `P${String(puntoVentaRaw).padStart(3, '0')}`;
  
  // Código de establecimiento MH (4 caracteres para el numeroControl)
  // El formato MH es: DTE-{tipoDte}-{codEstableMH}-{codPuntoVenta}-{correlativo}
  const codEstableMH = selectedBranch?.codigo_establecimiento
    ? selectedBranch.codigo_establecimiento.substring(0, 4).toUpperCase()
    : 'M001';
  
  // Código de establecimiento del contribuyente (4 caracteres)
  const codEstable = document.header?.sucursal
    ? String(document.header.sucursal).padStart(4, '0').substring(0, 4)
    : '0001';
  
  const emisor = {
    nit: (company?.nit || '').replace(/-/g, ''),  // Quitar guiones del NIT - debe tener 14 dígitos
    nrc: company?.nrc || '',
    nombre: company?.legal_name || company?.nombre || 'CONAMYPE',
    codActividad: company?.economic_activity_code || '',
    descActividad: company?.economic_activity_desc || '',
    nombreComercial: company?.commercial_name || '',
    direccion: {
      // Departamento: 2 dígitos (código de departamento)
      // El código viene como 4 dígitos (0601) pero solo necesitamos los 2 primeros para el departamento
      // Según CAT-012: 06 = San Salvador
      departamento: String(company?.main_departamento_code || '06').padStart(2, '0'),
      // Municipio: 2 dígitos (código del municipio dentro del departamento)
      // Si viene como "0601" (4 dígitos), extraer solo los últimos 2 dígitos
      // Según CAT-012: 01 = San Salvador (dentro del dept. 06)
      municipio: String(company?.main_municipio_code || '01').slice(-2).padStart(2, '0'),
      distrito: String(company?.main_distrito_code || company?.main_district_code || ''),
      complemento: company?.main_address || ''
    },
    telefono: company?.bill_phone || company?.phone || '',
    correo: company?.bill_email || company?.email || '',
    codEstable,
    codPuntoVenta
  };
  
  // Determinar la versión según el tipo de documento
  const getVersion = (tipo) => {
    const versionMap = {
      '01': 2,
      '03': 4,
      '04': 4,
      '05': 4,
      '06': 4,
      '07': 2,
      '08': 2,
      '09': 2,
      '11': 3,
      '14': 2,
      '15': 2,
    };
    return versionMap[tipo] || 1;
  };

  const version = getVersion(tipoDte);
  
  const identificacion = {
    version,
    ambiente: company?.environment === 'production' ? '01' : '00',
    tipoDte,
	    numeroControl: document.identificacion?.numeroControl || generateNumeroControl(tipoDte, codEstableMH, codPuntoVenta, 1),
    codigoGeneracion: crypto.randomUUID().toUpperCase(),
    tipoModelo: 1,
    tipoOperacion: 1,
    tipoContingencia: null,
    motivoContin: null,
    fecEmi,
    horEmi,
    tipoMoneda: 'USD'
  };
  
  let receptor = null;
  let sujetoExcluido = null;
  
  // Determinar la estructura según el tipo de documento
  if (tipoDte === '14' || tipoDte === '15') {
    // Sujeto Excluido o Donación
    sujetoExcluido = {
      tipoDocumento: document.receptor?.tipoDocumento || '13',
      numDocumento: document.receptor?.numDocumento || '',
      nombre: document.receptor?.nombre || '',
      codActividad: document.receptor?.codActividad || '',
      descActividad: document.receptor?.descActividad || '',
      direccion: document.receptor?.direccion || null,
      telefono: document.receptor?.telefono || document.receptor?.whatsapp || '',
      correo: document.receptor?.correoNotificacion || null
    };
  } else if (tipoDte === '07') {
    // CR (Comprobante de Retención) - receptor NO tiene campo nit separado
    // IMPORTANTE: El tipoDocumento puede venir como string o número, normalizar a string para comparar
    const tipoDocReceptor = String(document.receptor?.tipoDocumento || '');
    const esNIT = tipoDocReceptor === '36';
    
    // IMPORTANTE: municipio debe ser solo 2 dígitos (no 4 como viene del catálogo)
    const receptorMunicipio = document.receptor?.direccion?.municipio 
      ? String(document.receptor.direccion.municipio).slice(-2).padStart(2, '0') 
      : null;
    
    receptor = {
      tipoDocumento: String(document.receptor?.tipoDocumento || ''),
      numDocumento: document.receptor?.numDocumento || '',
      nrc: document.receptor?.nrc || '',
      nombre: document.receptor?.nombre || '',
      codActividad: document.receptor?.codActividad || '',
      descActividad: document.receptor?.descActividad || '',
      nombreComercial: document.receptor?.nombreComercial || null,
      direccion: document.receptor?.direccion?.departamento ? {
        departamento: String(document.receptor.direccion.departamento).slice(-2).padStart(2, '0'),
        municipio: receptorMunicipio,
        // Durante la migración, el municipio anterior representa el distrito.
        // El backend convierte después municipio/distrito al nuevo catálogo MH.
        distrito: String(document.receptor.direccion.distrito || receptorMunicipio || ''),
        complemento: document.receptor?.direccion?.complemento || ''
      } : null,
      telefono: document.receptor?.telefono || '',
      correo: document.receptor?.correoNotificacion || ''
    };
  } else if (tipoDte === '03' || tipoDte === '05' || tipoDte === '06' || tipoDte === '08' || tipoDte === '09') {
    // CCF, CLE, DCLE - requieren NRC y nit
    // IMPORTANTE: El tipoDocumento puede venir como string o número, normalizar a string para comparar
    const tipoDocReceptor = String(document.receptor?.tipoDocumento || '');
    const esNIT = tipoDocReceptor === '36';
    
    // IMPORTANTE: municipio debe ser solo 2 dígitos (no 4 como viene del catálogo)
    const receptorMunicipio = document.receptor?.direccion?.municipio 
      ? String(document.receptor.direccion.municipio).slice(-2).padStart(2, '0') 
      : null;
    
    receptor = {
      nit: esNIT ? (document.receptor?.numDocumento || '').replace(/-/g, '') : '',
      nrc: document.receptor?.nrc || '',
      nombre: document.receptor?.nombre || '',
      codActividad: document.receptor?.codActividad || '',
      descActividad: document.receptor?.descActividad || '',
      nombreComercial: document.receptor?.nombreComercial || null,
      direccion: document.receptor?.direccion?.departamento ? {
        departamento: String(document.receptor.direccion.departamento).slice(-2).padStart(2, '0'),
        municipio: receptorMunicipio,
        complemento: document.receptor?.direccion?.complemento || ''
      } : null,
      telefono: document.receptor?.telefono || '',
      correo: document.receptor?.correoNotificacion || ''
    };
  } else {
    // Factura Electrónica, NRE, NC, ND, FEX
    // Para consumidor final sin identificar: tipoDocumento="36", nrc=null, direccion=null
    // Nota: si el receptor tiene datos válidos, usarlos; si no, usar valores por defecto
    const tieneReceptorValido = document.receptor && 
      document.receptor.tipoDocumento && 
      document.receptor.numDocumento && 
      document.receptor.nombre;
    
      // Para consumidor final: usar null para dirección si no hay receptor válido
      // IMPORTANTE: La dirección debe tener departamento y municipio válidos
      // Si alguno es null, enviar null para evitar errores de validación MH
      // IMPORTANTE: municipio y departamento deben ser solo 2 dígitos (no 4 como viene del catálogo)
      const tieneDireccion = tieneReceptorValido && 
        document.receptor.direccion && 
        document.receptor.direccion.departamento && 
        document.receptor.direccion.municipio;
      
      const receptorMunicipio = tieneDireccion && document.receptor.direccion.municipio
        ? String(document.receptor.direccion.municipio).slice(-2).padStart(2, '0')
        : null;
      
      const receptorTipoDocumento = tieneReceptorValido ? String(document.receptor.tipoDocumento) : '36';
      const rawNumDocumento = tieneReceptorValido ? String(document.receptor.numDocumento || '') : '00000000-0';
      const normalizedNumDocumento = receptorTipoDocumento === '36'
        ? rawNumDocumento.replace(/\D/g, '')
        : rawNumDocumento;

      const nrcDigits = tieneReceptorValido ? String(document.receptor.nrc || '').replace(/\D/g, '') : '';
      const telefonoValue = tieneReceptorValido ? (document.receptor.telefono || document.receptor.whatsapp || '') : '';
      const correoValue = tieneReceptorValido ? (document.receptor.correoNotificacion || document.receptor.correo || '') : '';

      receptor = {
        tipoDocumento: receptorTipoDocumento,
        numDocumento: normalizedNumDocumento,
        nrc: nrcDigits ? nrcDigits : null,
        nombre: tieneReceptorValido ? document.receptor.nombre : 'CONSUMIDOR FINAL',
        codActividad: tieneReceptorValido && document.receptor.codActividad ? document.receptor.codActividad : null,
        descActividad: tieneReceptorValido && document.receptor.descActividad ? document.receptor.descActividad : null,
        direccion: tieneDireccion ? {
          departamento: String(document.receptor.direccion.departamento).slice(-2).padStart(2, '0'),
          municipio: receptorMunicipio,
          distrito: String(document.receptor.direccion.distrito || ''),
          complemento: document.receptor.direccion.complemento || ''
        } : null,
        telefono: telefonoValue ? telefonoValue : null,
        correo: correoValue ? correoValue : null
      };
  }
  
  const cuerpoDocumento = document.cuerpoDocumento.map((item, index) => {
    const cantidad = parseFloat(item.cantidad) || 1;
    
    // Tipos con estructura de Comprobante de Retención (07)
    // CR usa los nombres del contrato MH v2.
    if (tipoDte === '07') {
      const montoSujetoGrav = Math.round((parseFloat(item.montoSujetoGrav) || 0) * 100) / 100;
      const { ivaRetenido } = calculateRetentionAmounts(
        montoSujetoGrav,
        item.codigoRetencionMH,
        item.porcentajeEspecial,
      );
      return {
        numItem: index + 1,
        tipoDte: item.tipoDte || '01',  // Tipo de documento referido (01=FE, 03=CCF, 14=FSE)
        tipoGeneracion: item.tipoGeneracion || 1,
        numeroDocumento: item.numeroDocumento || '',
        fechaEmision: item.fechaEmision || '',
        montoSujetoGrav,
        codigoRetencionMH: item.codigoRetencionMH || '',
        descripcion: item.descripcion || '',
        ivaRetenido
      };
    }
    // Tipos con estructura de Sujeto Excluido (14, 15)
    if (tipoDte === '14' || tipoDte === '15') {
      const compraTotal = (parseFloat(item.precioUni) || 0) * cantidad - (parseFloat(item.montoDescu) || 0) * cantidad;
      return {
        numItem: index + 1,
        tipoItem: item.tipoItem || 1,
        cantidad,
        codigo: item.codigo || '',
        uniMedida: item.uniMedida || 59,
        descripcion: item.descripcion || '',
        precioUni: parseFloat((parseFloat(item.precioUni) || 0).toFixed(8)),  // Máximo 8 decimales
        montoDescu: parseFloat(item.montoDescu) || 0,
        compra: compraTotal,
        tributos: null,
        noGravado: 0
      };
    } 
    // Tipos con estructura de CCF (03, 08, 09)
    else if (tipoDte === '03' || tipoDte === '08' || tipoDte === '09') {
      // IMPORTANTE: Redondear precioSinIva a 8 decimales ANTES de calcular
      const precioSinIva = Math.round(parseFloat(item.precioUni) * 100000000) / 100000000 || 0;
      const cantidad = parseFloat(item.cantidad) || 1;
      const montoDescu = parseFloat(item.montoDescu) || 0;
      // Redondear ventaGravada a 8 decimales
      const ventaGravada = Math.round((precioSinIva * cantidad - montoDescu * cantidad) * 100000000) / 100000000;
      
      // DEBUG: Loguear valores para diagnóstico
      console.log('[DEBUG CCF] precioUni original:', item.precioUni, 'precioSinIva:', precioSinIva, 'cantidad:', cantidad, 'ventaGravada:', ventaGravada);
      
      // Calcular IVA para el resumen (ventaGravada * 0.13)
      // IMPORTANTE: MH requiere exactamente 8 decimales (1.0E-8)
      // Usar Math.round para asegurar que el valor tenga exactamente 8 decimales
      const ivaItem = Math.round(ventaGravada * 0.13 * 100000000) / 100000000;
      
      console.log('[DEBUG CCF] ivaItem calculado para resumen:', ivaItem);
      
	      return {
	        numItem: index + 1,
	        tipoItem: item.tipoItem || 1,
	        numeroDocumento: isNotaCreditoDebito ? (document.documentoRelacionadoBase?.codigoGeneracion || '') : null,
	        cantidad,
	        codigo: item.codigo || '',
	        codTributo: null,
	        uniMedida: item.uniMedida || 13,
        descripcion: item.descripcion || '',
        precioUni: precioSinIva,  // Ya redondeado a 8 decimales
        montoDescu,
        ventaNoSuj: 0,
        ventaExenta: 0,
        ventaGravada,  // Ya redondeado a 8 decimales
        tributos: ["20"],  // Campo correcto según schema MH: array con código de tributo IVA 13%
        psv: 0,
        noGravado: 0
      };
    } 
    // Factura de Exportación (11)
    else if (tipoDte === '11') {
      const precioConIva = parseFloat(item.precioConIva) || 0;
      const precioSinIva = precioConIva; // Sin IVA para exportación
      const ventaGravada = precioSinIva * cantidad - (parseFloat(item.montoDescu) || 0) * cantidad;
      return {
        numItem: index + 1,
        tipoItem: item.tipoItem || 1,
        numeroDocumento: null,
        cantidad,
        codigo: item.codigo || '',
        codTributo: null,
        uniMedida: item.uniMedida || 59,
        descripcion: item.descripcion || '',
        precioUni: parseFloat(precioSinIva.toFixed(8)),  // Máximo 8 decimales
        montoDescu: parseFloat(item.montoDescu) || 0,
        ventaNoSuj: 0,
        ventaExenta: ventaGravada,
        ventaGravada: 0,
        tributos: null,
        psv: 0,
        noGravado: 0
      };
    }
    // Otros tipos: FE, NRE, NC, ND (precios con IVA)
    else {
      const precioConIva = parseFloat(item.precioConIva) || 0;
      const cantidad = parseFloat(item.cantidad) || 1;
      const montoDescu = parseFloat(item.montoDescu) || 0;
      // IMPORTANTE: Redondear precioSinIva a 8 decimales ANTES de calcular
      const precioSinIva = Math.round((precioConIva / 1.13) * 100000000) / 100000000;
      // Redondear ventaGravada a 8 decimales
      const ventaGravada = Math.round((precioSinIva * cantidad - montoDescu * cantidad) * 100000000) / 100000000;
      
      // DEBUG: Loguear valores para diagnóstico
      console.log('[DEBUG FE] precioConIva:', precioConIva, 'precioSinIva:', precioSinIva, 'cantidad:', cantidad, 'montoDescu:', montoDescu, 'ventaGravada:', ventaGravada);
      
      // Calcular IVA para el resumen (ventaGravada * 0.13)
      // IMPORTANTE: MH requiere exactamente 8 decimales (1.0E-8)
      // Usar Math.round para asegurar que el valor tenga exactamente 8 decimales
      const ivaItem = Math.round(ventaGravada * 0.13 * 100000000) / 100000000;
      
      console.log('[DEBUG FE] ivaItem calculado para resumen:', ivaItem);
      
      return {
        numItem: index + 1,
        tipoItem: item.tipoItem || 1,
        numeroDocumento: null,
        cantidad,
        codigo: item.codigo || '',
        codTributo: null,
        uniMedida: item.uniMedida || 13,
        descripcion: item.descripcion || '',
        precioUni: precioSinIva,  // Ya redondeado a 8 decimales
        montoDescu,
        ventaNoSuj: 0,
        ventaExenta: 0,
        ventaGravada,  // Ya redondeado a 8 decimales
        tributos: null,
        psv: 0,
        noGravado: 0
      };
    }
  });
  
  let resumen = {};
  
  // Resumen para Sujeto Excluido y Donación (14, 15)
  if (tipoDte === '14' || tipoDte === '15') {
    const totalCompra = cuerpoDocumento.reduce((sum, item) => sum + (item.compra || 0), 0);
    const reteRenta = totalCompra * 0.10;
    const totalPagar = totalCompra - reteRenta;
    resumen = {
      totalCompra,
      descu: 0,
      totalDescu: 0,
      subTotal: totalCompra,
      ivaRete1: 0,
      reteRenta,
      totalPagar,
      totalLetras: numberToWords(totalPagar),
      condicionOperacion: document.header?.formaPago === 'credito' ? 2 : 1,
      pagos: [{ codigo: document.header?.metodoPago || '01', montoPago: totalPagar, referencia: null, plazo: null, periodo: null }],
      observaciones: tipoDte === '14' 
        ? 'Compra de productos/servicios a persona natural no inscrita en IVA. Retención de renta del 10% aplicada según Art. 156 del Código Tributario.'
        : 'Donación recibida según certificación de calidad de donante.'
    };
  } 
  // Resumen para CCF, NC, ND, CLE, DCLE (03, 05, 06, 08, 09)
  else if (tipoDte === '03' || tipoDte === '05' || tipoDte === '06' || tipoDte === '08' || tipoDte === '09') {
    // IMPORTANTE: Redondear cada campo del resumen a 2 decimales exactos
    const totalGravada = Math.round(cuerpoDocumento.reduce((sum, item) => sum + (item.ventaGravada || 0), 0) * 100) / 100;
    // Calcular IVA desde ventaGravada (13%)
    const totalIva = Math.round(totalGravada * 0.13 * 100) / 100;
    const totalDescu = Math.round(cuerpoDocumento.reduce((sum, item) => sum + ((item.montoDescu || 0) * item.cantidad), 0) * 100) / 100;
    // subTotal = subTotalVentas - totalDescu (según fórmula MH)
    const subTotal = Math.round((totalGravada - totalDescu) * 100) / 100;
    // montoTotalOperacion = subTotal + IVA
    const montoTotalOperacion = Math.round((subTotal + totalIva) * 100) / 100;
    const totalPagar = montoTotalOperacion;
    
    console.log('[DEBUG RESUMEN CCF] totalGravada:', totalGravada, 'totalIva:', totalIva, 'subTotal:', subTotal, 'montoTotalOperacion:', montoTotalOperacion);
    
    resumen = {
      totalNoSuj: 0, totalExenta: 0, totalGravada, subTotalVentas: totalGravada,
      descuNoSuj: 0, descuExenta: 0, descuGravada: totalDescu, porcentajeDescuento: 0, totalDescu,
      tributos: [{ codigo: '20', descripcion: 'Impuesto al Valor Agregado 13%', valor: totalIva }],
      subTotal,  // Ahora es subTotalVentas - totalDescu
      ivaPerci1: 0,  // Campo requerido por schema
      ivaRete1: 0, reteRenta: 0, montoTotalOperacion,
      totalNoGravado: 0, totalPagar, totalLetras: numberToWords(totalPagar),
      saldoFavor: 0, condicionOperacion: document.header?.formaPago === 'credito' ? 2 : 1,
      pagos: [{ codigo: document.header?.metodoPago || '01', montoPago: totalPagar, referencia: null, plazo: null, periodo: null }],
      numPagoElectronico: null
    };
  }
  // Resumen para Comprobante de Retención (07)
  else if (tipoDte === '07') {
    // CR usa montoSujetoGrav e ivaRetenido en lugar de ventaGravada e ivaItem
    const totalSujetoRetencion = Math.round(cuerpoDocumento.reduce((sum, item) => sum + (item.montoSujetoGrav || 0), 0) * 100) / 100;
    const totalIva = Math.round(cuerpoDocumento.reduce(
      (sum, item) => sum + (item.codigoRetencionMH === 'C4' ? (item.ivaRetenido || 0) : 0),
      0,
    ) * 100) / 100;
    const totalIvaRetenido = Math.round(cuerpoDocumento.reduce((sum, item) => sum + (item.ivaRetenido || 0), 0) * 100) / 100;
    resumen = {
      totalSujetoRetencion,
      totalIva,
      totalIvaRetenido,
      totalLetras: numberToWords(totalIvaRetenido),
      observaciones: document.header?.observaciones || null,
    };
  }
  // Resumen para Factura de Exportación (11)
  else if (tipoDte === '11') {
    const totalExenta = cuerpoDocumento.reduce((sum, item) => sum + (item.ventaExenta || 0), 0);
    const montoTotalOperacion = totalExenta;
    resumen = {
      totalNoSuj: 0, totalExenta, totalGravada: 0, subTotalVentas: totalExenta,
      descuNoSuj: 0, descuExenta: 0, descuGravada: 0, porcentajeDescuento: 0, totalDescu: 0,
      tributos: null, subTotal: totalExenta, ivaRete1: 0, reteRenta: 0,
      montoTotalOperacion, totalNoGravado: 0, totalPagar: montoTotalOperacion,
      totalLetras: numberToWords(montoTotalOperacion), totalIva: 0,
      saldoFavor: 0, condicionOperacion: document.header?.formaPago === 'credito' ? 2 : 1,
      pagos: [{ codigo: document.header?.metodoPago || '01', montoPago: montoTotalOperacion, referencia: null, plazo: null, periodo: null }],
      numPagoElectronico: null
    };
  }
  // Resumen para Factura Electrónica, NRE, NC, ND (01, 04, 05, 06)
  else {
    const totalGravada = Math.round(cuerpoDocumento.reduce((sum, item) => sum + (item.ventaGravada || 0), 0) * 100) / 100;
    // Calcular IVA desde ventaGravada (13%)
    const totalIva = Math.round(totalGravada * 0.13 * 100) / 100;
    const montoTotalOperacion = Math.round((totalGravada + totalIva) * 100) / 100;
    const totalPagar = montoTotalOperacion;
    
    // Redondear montoPago a 2 decimales
    const montoPagoRedondeado = Math.round(montoTotalOperacion * 100) / 100;
    
    console.log('[DEBUG RESUMEN FE] totalGravada:', totalGravada, 'totalIva:', totalIva, 'montoTotalOperacion:', montoTotalOperacion);
    
    resumen = {
      totalNoSuj: 0, totalExenta: 0, totalGravada, subTotalVentas: totalGravada,
      descuNoSuj: 0, descuExenta: 0, descuGravada: 0, porcentajeDescuento: 0, totalDescu: 0,
      tributos: null, subTotal: montoTotalOperacion, ivaPerci1: 0,  // Campo requerido por schema
      ivaRete1: 0, reteRenta: 0,  // ivaRete1 es requerido
      montoTotalOperacion, totalNoGravado: 0, totalPagar,
      totalLetras: numberToWords(totalPagar), totalIva,
      saldoFavor: 0, condicionOperacion: document.header?.formaPago === 'credito' ? 2 : 1,
      pagos: [{ codigo: document.header?.metodoPago || '01', montoPago: montoPagoRedondeado, referencia: null, plazo: null, periodo: null }],
      numPagoElectronico: null
    };
  }
  
  const jsonFinal = {
    identificacion,
    emisor,
    receptor,
    cuerpoDocumento,
    resumen,
    ...(isNotaCreditoDebito
      ? {
          documentoRelacionado: [
            {
              tipoDocumento: document.documentoRelacionadoBase?.tipoDocumento || '',
              tipoGeneracion: 2,
              numeroDocumento: document.documentoRelacionadoBase?.codigoGeneracion || '',
              fechaEmision: document.documentoRelacionadoBase?.fechaEmision || '',
            },
          ],
          ventaTercero: null,
        }
      : {}),
    // El esquema CR v2 no define `extension`; enviarla (aunque sea null)
    // produce additionalProperties. Los demás esquemas sí la contemplan.
    ...(tipoDte === '07' ? {} : { extension: null }),
    apendice: null
  };

  // Debug: Loguear valores problemáticos
  console.log('═══════════════════════════════════════════════════════════');
  console.log('DEBUG JSON DTE - Valores problemáticos:');
  console.log('company (parámetro):', company ? 'EXISTE' : 'NULL');
  console.log('company.nit:', company?.nit);
  console.log('company.main_municipio_code:', company?.main_municipio_code);
  console.log('document.receptor:', document.receptor ? 'EXISTE' : 'NULL');
  console.log('numeroControl:', jsonFinal.identificacion.numeroControl);
  console.log('numeroControl length:', jsonFinal.identificacion.numeroControl.length);
  console.log('numeroControl partes:', jsonFinal.identificacion.numeroControl.split('-'));
  console.log('emisor:', JSON.stringify(jsonFinal.emisor));
  console.log('receptor:', JSON.stringify(jsonFinal.receptor));
  console.log('cuerpoDocumento length:', jsonFinal.cuerpoDocumento.length);
  console.log('resumen:', JSON.stringify(jsonFinal.resumen));
  console.log('═══════════════════════════════════════════════════════════');

  if (tipoDte === '14' || tipoDte === '15') {
    jsonFinal.sujetoExcluido = sujetoExcluido;
    delete jsonFinal.receptor;
  }

  return jsonFinal;
};


const DTEWizard = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [document, setDocument] = useState(getInitialDocumentState());
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [schemaDiagnostics, setSchemaDiagnostics] = useState([]);
  const wasAcceptedByMh = Boolean(getReceptionSeal(submitResult));
  const [whatsappCountry, setWhatsappCountry] = useState('503'); // El Salvador por defecto
  const [isSearchingClient, setIsSearchingClient] = useState(false);
  const [clienteMode, setClienteMode] = useState('existente'); // 'existente' o 'nuevo'
  const [expandedItems, setExpandedItems] = useState({}); // Track which items are expanded (avanzado)
  const [expandedSections, setExpandedSections] = useState({ header: false, detalle: true }); // Control accordions
  const [recentDocs, setRecentDocs] = useState([]);
  const [isLoadingRecentDocs, setIsLoadingRecentDocs] = useState(false);
  
  // Datos del emisor desde localStorage y backend
  const [emisorData, setEmisorData] = useState(null);
  
  // Cargar datos del emisor al iniciar - usando settingsService
  useEffect(() => {
    const loadEmisorData = async () => {
      try {
        // Primero cargar desde localStorage como fallback
        const emisor = getEmisorData();
        
        // Cargar datos completos desde el backend usando settingsService
        const companyId = getCurrentCompanyId();
        if (companyId) {
          const response = await settingsService.getCompany(companyId);
          if (response.success && response.data) {
            console.log('[DEBUG] Datos completos del emisor cargados desde settingsService:', response.data);
            setEmisorData(response.data);
          } else if (emisor) {
            // Fallback a datos de localStorage
            console.log('[DEBUG] Usando datos del emisor desde localStorage:', emisor);
            setEmisorData(emisor);
          }
        } else if (emisor) {
          console.log('[DEBUG] Usando datos del emisor desde localStorage:', emisor);
          setEmisorData(emisor);
        }
      } catch (error) {
        console.error('Error cargando datos del emisor:', error);
        // Intentar con localStorage como fallback
        const emisor = getEmisorData();
        if (emisor) {
          setEmisorData(emisor);
        }
      }
    };
    loadEmisorData();
  }, []);
  
  // Responsive
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Cargar catálogos de departamentos y municipios
  const [catalogs, setCatalogs] = useState({ departamentos: [], municipios: [] });
  const [branches, setBranches] = useState([]);
  const [pointsOfSale, setPointsOfSale] = useState([]);
  const getCurrentPosStorageKey = (companyId, branchCode) =>
    companyId && branchCode ? `nx2026_dte_pos_${companyId}_${branchCode}` : 'nx2026_dte_pos_default';
  const persistSelectedPos = (companyId, branchCode, posId) => {
    try {
      localStorage.setItem(getCurrentPosStorageKey(companyId, branchCode), String(posId || ''));
    } catch {
      // ignore
    }
  };
  const getPersistedSelectedPos = (companyId, branchCode) => {
    try {
      return localStorage.getItem(getCurrentPosStorageKey(companyId, branchCode)) || '';
    } catch {
      return '';
    }
  };
  
  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const res = await settingsService.getCatalogs();
        if (res.success) {
          setCatalogs({
            departamentos: res.data.departamentos || [],
            municipios: res.data.municipios || []
          });
        }
        
        // Cargar sucursales
        const companyId = getCurrentCompanyId();
	        if (companyId) {
	          const branchesRes = await settingsService.getBranches(companyId);
	          if (branchesRes.success) {
            const branchesData = branchesRes.data || [];
            setBranches(branchesData);
            
            // Seleccionar sucursal primaria por defecto
            const primaryBranch = branchesData.find(b => b.is_primary === 1);
            if (primaryBranch) {
              setDocument((prev) => ({
                ...prev,
                header: {
                  ...prev.header,
                  sucursal: primaryBranch.codigo_establecimiento
                }
              }));
	            }
	          }

            const posRes = await settingsService.getPOS(companyId);
            if (posRes.success) {
              setPointsOfSale(posRes.data || []);
            }
	        }
	      } catch (error) {
        console.error('Error cargando catálogos:', error);
      }
    };
    loadCatalogs();
  }, []);

  useEffect(() => {
    const tipoDte = document.identificacion.tipoDte;
    const isNotaCreditoDebito = tipoDte === '05' || tipoDte === '06';
    if (!isNotaCreditoDebito) return;

    const loadRecentDocs = async () => {
      setIsLoadingRecentDocs(true);
      try {
        const res = await authenticatedFetch('/api/dte/recent?limit=50');
        if (!res.ok) throw new Error('No se pudo cargar documentos recientes');
        const rows = await res.json();
        const filtered = (Array.isArray(rows) ? rows : []).filter((row) => !row?.cancel_success);
        setRecentDocs(filtered);
      } catch (error) {
        console.error('[DTEWizard] Error cargando documentos recientes:', error);
        setRecentDocs([]);
      } finally {
        setIsLoadingRecentDocs(false);
      }
    };

    loadRecentDocs();
  }, [document.identificacion.tipoDte]);

  // Filtrar municipios por departamento seleccionado
  const filteredMunicipios = useMemo(() => {
    if (!document.receptor?.direccion?.departamento) return [];
    return catalogs.municipios.filter(
      m => m.departamento_codigo === document.receptor.direccion.departamento
    );
  }, [catalogs.municipios, document.receptor?.direccion?.departamento]);

  // Hook de validaciones
  const {
    validarDUI,
    validarNIT,
    validarNRC,
    validarCarnetResidente,
    validarPasaporte,
    validarEmail,
    validarWhatsApp,
    validateDocument,
    getDocumentErrorMessage,
    validateReceptorName,
    validateReceptorNRC,
    validateReceptorContact,
    getSearchThreshold,
    cleanDocumentNumber,
  } = useDTEValidation();

  // Obtener configuración del tipo de documento actual
  const dteConfig = getDTETypeConfig(document.identificacion.tipoDte);
  const steps = dteConfig?.steps || ['selection', 'receptor', 'detail', 'summary'];
  const selectedBranch = branches.find((branch) => branch.codigo_establecimiento === document.header?.sucursal) || null;
  const availablePointsOfSale = useMemo(() => (
    pointsOfSale.filter((pos) =>
      pos.branch_id === selectedBranch?.id && pos.deleted_at == null && pos.is_active !== 0
    )
  ), [pointsOfSale, selectedBranch?.id]);
  const selectedPos = useMemo(() => (
    availablePointsOfSale.find((pos) => pos.id === document.header?.posId) || null
  ), [availablePointsOfSale, document.header?.posId]);

  useEffect(() => {
    if (!selectedBranch) return;

    const companyId = getCurrentCompanyId();
    const branchCode = selectedBranch.codigo_establecimiento;
    const currentPos = availablePointsOfSale.find((pos) => pos.id === document.header?.posId) || null;
    const persistedPosId = getPersistedSelectedPos(companyId, branchCode);
    const persistedPos = availablePointsOfSale.find((pos) => pos.id === persistedPosId) || null;
    const nextPos = currentPos || persistedPos || (availablePointsOfSale.length === 1 ? availablePointsOfSale[0] : null);
    const nextPosId = nextPos?.id || null;
    const nextPuntoVenta = nextPos?.codigo_punto_venta || '';

    setDocument((prev) => {
      if (prev.header?.posId === nextPosId && normalizePosValue(prev.header?.puntoVenta) === normalizePosValue(nextPuntoVenta)) {
        return prev;
      }

      return {
        ...prev,
        header: {
          ...prev.header,
          posId: nextPosId,
          puntoVenta: nextPuntoVenta,
        },
      };
    });

    if (nextPosId) {
      persistSelectedPos(companyId, branchCode, nextPosId);
    }
  }, [availablePointsOfSale, document.header?.posId, selectedBranch]);

  // Longitudes máximas de documentos según tipo (usando configuración)
  const DOCUMENT_MAX_LENGTHS = {
    '36': 17, // NIT: 14 dígitos + 3 guiones = 17 caracteres
    '13': 10,  // DUI: 8 dígitos + guión + 1 dígito = 10
    '02': 7, // Carnet Residente: 7 caracteres
    '03': 8, // Pasaporte: 8 caracteres
    '37': 15, // Otro: hasta 15 caracteres
  };

  // Buscar cliente por documento en el backend
  const searchClientByDocument = async (tipoDoc, numDocumento) => {
    if (!tipoDoc || !numDocumento) return;
    
    const maxLength = DOCUMENT_MAX_LENGTHS[tipoDoc];
    if (!maxLength) return;
    
    // Calcular el 50% de la longitud máxima
    const threshold = Math.ceil(maxLength * 0.5);
    
    // Obtener solo los dígitos/caracteres sin máscara
    const cleanNumber = numDocumento.replace(/[^0-9A-Za-z]/g, '');
    
    // Solo buscar si supera el 50% de caracteres
    if (cleanNumber.length < threshold) return;
    
    const companyId = getCurrentCompanyId();
    
    setIsSearchingClient(true);
    try {
      // Incluir companyId en la query
      const url = companyId 
        ? `/api/clientes/buscar/${encodeURIComponent(cleanNumber)}?companyId=${companyId}`
        : `https://nexai-billing-api.lmendoza-sv.workers.dev/api/clientes/buscar/${encodeURIComponent(cleanNumber)}`;
      const response = await authenticatedFetch(url);
      if (response.ok) {
        const cliente = await response.json();
        // Complementar los datos del cliente
        setDocument((prev) => ({
          ...prev,
          receptor: {
            ...prev.receptor,
            nombre: cliente.nombre || prev.receptor?.nombre,
            nombreComercial: cliente.nombreComercial || cliente.nombre || prev.receptor?.nombre,
            correoNotificacion: cliente.email || prev.receptor?.correoNotificacion,
            telefono: cliente.telefono || prev.receptor?.telefono,
            // Si el tipo de documento es NIT y el cliente tiene NRC, agregarlo
            ...(tipoDoc === '36' && cliente.nrc ? { nrc: cliente.nrc } : {}),
          },
        }));
      }
    } catch (error) {
      console.log('Cliente no encontrado o error en búsqueda:', error.message);
    } finally {
      setIsSearchingClient(false);
    }
  };

  // Manejar selección de receptor desde ReceptorSearch
  const handleReceptorSelect = (receptor) => {
    console.log('[DEBUG] DTEWizard handleReceptorSelect - receptor recibido:', receptor);
    console.log('[DEBUG] DTEWizard handleReceptorSelect - tipoDocumento:', receptor?.tipoDocumento);
    console.log('[DEBUG] DTEWizard handleReceptorSelect - numDocumento:', receptor?.numDocumento);
    console.log('[DEBUG] DTEWizard handleReceptorSelect - nombre:', receptor?.nombre);
    console.log('[DEBUG] DTEWizard handleReceptorSelect - nrc:', receptor?.nrc);
    console.log('[DEBUG] DTEWizard handleReceptorSelect - correo:', receptor?.correo);
    
    if (receptor) {
      console.log('[DEBUG] DTEWizard handleReceptorSelect - tipoDocumento:', receptor.tipoDocumento);
      console.log('[DEBUG] DTEWizard handleReceptorSelect - numDocumento:', receptor.numDocumento);
      console.log('[DEBUG] DTEWizard handleReceptorSelect - nombre:', receptor.nombre);
      console.log('[DEBUG] DTEWizard handleReceptorSelect - correo:', receptor.correo);
      console.log('[DEBUG] DTEWizard handleReceptorSelect - nrc:', receptor.nrc);
      console.log('[DEBUG] DTEWizard handleReceptorSelect - codActividad:', receptor.codActividad);
      console.log('[DEBUG] DTEWizard handleReceptorSelect - direccion:', receptor.direccion);
      console.log('[DEBUG] DTEWizard handleReceptorSelect - telefono:', receptor.telefono);
      
      // Determinar el número de teléfono (puede venir con o sin código de país)
      let telefonoValue = receptor.telefono || '';
      // Normalizar teléfono para cumplir schema (solo dígitos, sin prefijo país)
      const digitsOnly = String(telefonoValue).replace(/\D/g, '');
      const countryPrefixes = COUNTRIES.map(c => c.prefix);
      const regexPrefix = new RegExp(`^(${countryPrefixes.join('|')})+`, '');
      telefonoValue = digitsOnly.replace(regexPrefix, '');
      if (telefonoValue.length > 8) {
        telefonoValue = telefonoValue.slice(-8);
      }
      
      setDocument((prev) => ({
        ...prev,
        receptor: {
          ...prev.receptor,
          id: (receptor.id && receptor.id !== 'undefined') ? receptor.id : null,
          tipoDocumento: receptor.tipoDocumento || prev.receptor?.tipoDocumento,
          numDocumento: receptor.numDocumento || prev.receptor?.numDocumento,
          nombre: receptor.nombre || '',
          nombreComercial: receptor.nombreComercial || '',
          nrc: receptor.nrc || '',
          correo: receptor.correo || '',
          correoNotificacion: receptor.correo || receptor.correoNotificacion || '',
          telefono: telefonoValue,
          whatsapp: telefonoValue, // También guardar en whatsapp para FE/SE
          direccion: receptor.direccion || null,
          codActividad: receptor.codActividad || '',
          descActividad: receptor.descActividad || '',
        },
      }));
    } else {
      // Limpiar campos del receptor cuando se recibe null
      console.log('[DEBUG] DTEWizard handleReceptorSelect - limpiando receptor');
      setDocument((prev) => ({
        ...prev,
        receptor: {
          id: null,
          tipoDocumento: prev.receptor?.tipoDocumento || '',
          numDocumento: '',
          nombre: '',
          nombreComercial: '',
          nrc: '',
          correo: '',
          correoNotificacion: '',
          telefono: '',
          direccion: null,
          codActividad: '',
          descActividad: '',
        },
      }));
    }
  };



  // Manejar cambio de tipo de DTE
  const handleTipoDteChange = (tipoDte) => {
    const dteType = getDTETypeConfig(tipoDte);
    setDocument((prev) => ({
      ...prev,
      identificacion: {
        ...prev.identificacion,
        tipoDte,
        version: ({ '01': 2, '03': 4, '04': 4, '05': 4, '06': 4, '07': 2, '08': 2, '09': 2, '11': 3, '14': 2, '15': 2 })[tipoDte] || 1,
      },
      documentoRelacionadoBase: null,
      receptor: dteType?.requiresReceptor ? {} : null, // Reset según configuración
      cuerpoDocumento: [],
    }));
    setClienteMode('existente'); // Reset a modo existente
    // Auto-avanzar al siguiente paso (Datos del Receptor)
    setActiveStep(1);
  };

  // Cambiar entre modo cliente existente y nuevo
  const handleClienteModeChange = (mode) => {
    setClienteMode(mode);
    if (mode === 'nuevo') {
      // Limpiar receptor para modo nuevo
      setDocument((prev) => ({
        ...prev,
        receptor: {
          tipoDocumento: prev.receptor?.tipoDocumento || '',
          numDocumento: '',
          nombre: '',
          nombreComercial: '',
          nrc: '',
          correo: '',
          correoNotificacion: '',
          telefono: '',
          direccion: null,
          codActividad: '',
          descActividad: '',
        },
      }));
    }
  };

  // Manejar cambio en campos anidados
  const handleChange = (section, field, value, subField = null) => {
    setDocument((prev) => ({
      ...prev,
      [section]: subField
        ? {
            ...prev[section],
            [field]: {
              ...prev[field],
              [subField]: value,
            },
          }
        : {
            ...prev[section],
            [field]: value,
          },
    }));
    // Limpiar error
    if (errors[`${section}.${field}`]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`${section}.${field}`];
        return newErrors;
      });
    }
  };

  // Agregar línea al cuerpo del documento
  const handleAddItem = () => {
    const tipoDte = document.identificacion?.tipoDte;
    const newItem = createNewItem(document.cuerpoDocumento.length + 1, tipoDte);
    setDocument((prev) => ({
      ...prev,
      cuerpoDocumento: [...prev.cuerpoDocumento, newItem],
    }));
  };

  // Eliminar línea del cuerpo del documento
  const handleRemoveItem = (index) => {
    setDocument((prev) => ({
      ...prev,
      cuerpoDocumento: prev.cuerpoDocumento.filter((_, i) => i !== index),
    }));
  };

  // Actualizar línea del cuerpo del documento
  const handleItemChange = (index, field, value) => {
    setDocument((prev) => ({
      ...prev,
      cuerpoDocumento: prev.cuerpoDocumento.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  // Toggle item avanzado
  const toggleItemAdvanced = (index) => {
    setExpandedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Actualizar header del documento
  const handleHeaderChange = (field, value) => {
    setDocument((prev) => ({
      ...prev,
      header: {
        ...prev.header,
        [field]: value,
      },
    }));
  };

  // Validar paso actual
  // IMPORTANTE: Los campos requeridos se cargan DESDE EL JSON SCHEMA, no de dteConfig.js
  // Mapeo de campos del schema a campos del formulario
  const SCHEMA_TO_FORM_FIELD_MAP = {
    'nit': 'numDocumento',  // nit del schema = numDocumento del formulario (cuando tipoDocumento es NIT)
    'nrc': 'nrc',
    'nombre': 'nombre',
    'codActividad': 'codActividad',
    'descActividad': 'descActividad',
    'nombreComercial': 'nombreComercial',
    'telefono': 'telefono',
    'correo': 'correoNotificacion',
    'direccion': 'direccion'  // Objeto completo, requiere validar campos anidados
  };
  
  // Campos requeridos para la dirección (del schema)
  const DIRECCION_REQUIRED_FIELDS = ['departamento', 'municipio', 'complemento'];

  const validateStep = (step) => {
    const newErrors = {};
    const tipoDte = document.identificacion.tipoDte;
    const isNotaCreditoDebito = tipoDte === '05' || tipoDte === '06';
    
    // Obtener campos requeridos del receptor desde el JSON schema
    const schemaRequiredFields = getRequiredFields(tipoDte, 'receptor') || [];
    console.log('[DEBUG validateStep] Required fields from JSON schema for receptor:', schemaRequiredFields);
    
    // DEBUG: Loguear qué campos se validan
    console.log('[DEBUG validateStep] Step:', step, 'TipoDTE:', tipoDte);
    console.log('[DEBUG validateStep] receptor:', JSON.stringify(document.receptor));
    console.log('[DEBUG validateStep] receptor:', JSON.stringify(document.receptor));

    switch (step) {
      case 0: // Tipo de documento
        if (!document.identificacion.tipoDte) {
          newErrors['identificacion.tipoDte'] = 'Seleccione un tipo de documento';
        }
        break;
      case 1: // Receptor
        if (isNotaCreditoDebito && !document.documentoRelacionadoBase?.codigoGeneracion) {
          newErrors['documentoRelacionado'] = 'Seleccione el documento relacionado';
        }
        // Continuar con validaciones adicionales específicas del formulario
        if (!document.receptor) {
          newErrors['receptor'] = 'Datos del receptor son requeridos';
        } else {
          // Validar campos requeridos según el tipo de DTE
          // Validar tipo de documento
          if (schemaRequiredFields.includes('tipoDocumento')) {
            if (!document.receptor.tipoDocumento) {
              newErrors['receptor.tipoDocumento'] = 'Seleccione el tipo de documento';
            }
          }
          
          // Validar número de documento
          if (schemaRequiredFields.includes('numDocumento')) {
            if (!document.receptor.numDocumento) {
              newErrors['receptor.numDocumento'] = 'Número de documento es requerido';
            } else if (document.receptor.tipoDocumento) {
              // Validar formato según tipo de documento
              const tipoDoc = document.receptor.tipoDocumento;
              const numDoc = document.receptor.numDocumento;
              
              if (tipoDoc === '36') { // NIT
                if (!validarNIT(numDoc)) {
                  newErrors['receptor.numDocumento'] = 'NIT inválido. Formato: ####-######-###-#';
                }
              } else if (tipoDoc === '13') { // DUI
                if (!validarDUI(numDoc)) {
                  newErrors['receptor.numDocumento'] = 'DUI inválido. Formato: ########-#';
                }
              } else if (tipoDoc === '02') { // Carnet Residente
                if (!validarCarnetResidente(numDoc)) {
                  newErrors['receptor.numDocumento'] = 'Carnet Residente inválido';
                }
              } else if (tipoDoc === '03') { // Pasaporte
                if (!validarPasaporte(numDoc)) {
                  newErrors['receptor.numDocumento'] = 'Pasaporte inválido';
                }
              } else if (tipoDoc === '37') { // Otro
                const cleanNum = numDoc.replace(/-/g, '');
                if (cleanNum.length < 3 || cleanNum.length > 20) {
                  newErrors['receptor.numDocumento'] = 'Documento debe tener entre 3 y 20 caracteres';
                }
              }
            }
          }
          
          // Validar nombre
          if (schemaRequiredFields.includes('nombre')) {
            if (!document.receptor.nombre) {
              newErrors['receptor.nombre'] = 'Nombre o razón social es requerido';
            } else if (document.receptor.nombre.length < 2) {
              newErrors['receptor.nombre'] = 'Nombre debe tener al menos 2 caracteres';
            }
          }
          
          // Validar NRC (para CCF y CR) - SÓLO si está en requiredFields Y es tipo CCF/CR
          if (schemaRequiredFields.includes('nrc')) {
            if (!document.receptor.nrc) {
              newErrors['receptor.nrc'] = 'NRC es requerido';
            } else if (!validarNRC(document.receptor.nrc)) {
              newErrors['receptor.nrc'] = 'NRC inválido. Solo números, máximo 8 dígitos';
            }
          }
          
          // Validar NIT (para CCF)
          if (schemaRequiredFields.includes('nit')) {
            if (!document.receptor.numDocumento || document.receptor.tipoDocumento !== '36') {
              // NIT es requerido si el tipo de documento es NIT
              if (document.receptor.tipoDocumento === '36' && !document.receptor.numDocumento) {
                newErrors['receptor.numDocumento'] = 'NIT es requerido';
              }
            }
          }
          
          // Validar correo (para CCF y CR) - del schema: 'correo', mapea a 'correoNotificacion' en form
          if (schemaRequiredFields.includes('correo')) {
            if (!document.receptor.correoNotificacion) {
              newErrors['receptor.correoNotificacion'] = 'Correo es requerido';
            } else {
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (!emailRegex.test(document.receptor.correoNotificacion)) {
                newErrors['receptor.correoNotificacion'] = 'Ingrese un correo electrónico válido';
              }
            }
          }
          
          // Validar actividad económica - SÓLO si está en requiredFields Y es tipo CCF/CR
          if (schemaRequiredFields.includes('codActividad')) {
            if (!document.receptor.codActividad) {
              newErrors['receptor.codActividad'] = 'Actividad económica es requerida';
            }
          }
          
          // Validar departamento y municipio - SÓLO si está en requiredFields Y es tipo CCF/CR
          if (schemaRequiredFields.includes('departamento')) {
            if (!document.receptor.direccion?.departamento) {
              newErrors['receptor.direccion.departamento'] = 'Departamento es requerido';
            }
          }
          if (schemaRequiredFields.includes('municipio')) {
            if (!document.receptor.direccion?.municipio) {
              newErrors['receptor.direccion.municipio'] = 'Municipio es requerido';
            }
          }
          
          // Validar dirección - SÓLO si está en requiredFields Y es tipo CCF/CR
          if (schemaRequiredFields.includes('direccion')) {
            const direccionRequiredFields = getRequiredFields(tipoDte, 'receptor.direccion');
            direccionRequiredFields.forEach((field) => {
              if (!document.receptor.direccion?.[field]) {
                newErrors[`receptor.direccion.${field}`] = `${field} es requerido`;
              }
            });
          }
        }
        
        // DEBUG: Loguear errores encontrados
        if (Object.keys(newErrors).length > 0) {
          console.log('[DEBUG validateStep] Errores encontrados:', newErrors);
        }
        break;
	      case 2: // Cuerpo del documento
        if (!selectedBranch) {
          newErrors['header.sucursal'] = 'Seleccione una sucursal';
        }
        if (!availablePointsOfSale.length) {
          newErrors['header.posId'] = 'No hay cajas configuradas para esta sucursal';
        } else if (!selectedPos) {
          newErrors['header.posId'] = 'Seleccione una caja';
        }
	        // Si es un documento con referencia (CR), usar validación específica
	        if (hasDocumentReference(tipoDte)) {
          // Validar campos específicos de CR
          if (document.cuerpoDocumento.length === 0) {
            newErrors['cuerpoDocumento'] = 'Agregue al menos una retención';
          } else {
            document.cuerpoDocumento.forEach((item, index) => {
              // Validar número de documento
              if (!item.numeroDocumento || item.numeroDocumento.trim() === '') {
                newErrors[`cuerpoDocumento.${index}.numeroDocumento`] = 'Número de documento es requerido';
              }
              // Validar monto sujeto a gravamen (mínimo 0.01)
              if (!item.montoSujetoGrav || parseFloat(item.montoSujetoGrav) < 0.01) {
                newErrors[`cuerpoDocumento.${index}.montoSujetoGrav`] = 'Monto sujeto a gravamen es requerido (mín: 0.01)';
              }
              // Validar código de retención
              if (!item.codigoRetencionMH) {
                newErrors[`cuerpoDocumento.${index}.codigoRetencionMH`] = 'Código de retención es requerido';
              }
              if (
                item.codigoRetencionMH === 'C9'
                && (!Number.isFinite(Number(item.porcentajeEspecial))
                  || Number(item.porcentajeEspecial) <= 0
                  || Number(item.porcentajeEspecial) > 100)
              ) {
                newErrors[`cuerpoDocumento.${index}.porcentajeEspecial`] = 'Ingrese un porcentaje mayor que 0 y hasta 100';
              }
              // Validar descripción (requerida según schema MH)
              if (!item.descripcion || item.descripcion.trim() === '') {
                newErrors[`cuerpoDocumento.${index}.descripcion`] = 'Descripción es requerida';
              }
            });
          }
        } else {
          // Validación normal para documentos con items de productos
          if (document.cuerpoDocumento.length === 0) {
            newErrors['cuerpoDocumento'] = 'Agregue al menos un item';
          } else {
            // Validar que cada item tenga descripción y precio obligatorios
            document.cuerpoDocumento.forEach((item, index) => {
              if (!item.descripcion || item.descripcion.trim() === '') {
                newErrors[`cuerpoDocumento.${index}.descripcion`] = 'Descripción es requerida';
              }
              if (!item.precioConIva || item.precioConIva <= 0) {
                newErrors[`cuerpoDocumento.${index}.precioConIva`] = 'Precio con IVA es requerido';
              }
            });
          }
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Obtener companyId desde localStorage
  const getCurrentCompanyId = () => {
    try {
      const modulesStr = localStorage.getItem('nx2026_modules');
      if (!modulesStr) return null;
      
      const modules = JSON.parse(modulesStr);
      // Tomar la primera compañía disponible
      if (modules && modules.length > 0 && modules[0].company) {
        // Retornar el id de la company (puede ser numérico o UUID)
        return modules[0].company.id;
      }
      return null;
    } catch (error) {
      console.error('Error obteniendo companyId:', error);
      return null;
    }
  };

  // Guardar o actualizar cliente al pasar del paso de Receptor
  const saveReceptor = async () => {
    if (activeStep !== 1 || !document.receptor) return null;
    
    const receptor = document.receptor;
    const companyId = getCurrentCompanyId();
    
    // Combinar código de país + teléfono para guardar
    // Primero verificar si hay WhatsApp (para FE/SE), luego Teléfono (para CCF/CR)
    const telefonoIngresado = receptor.whatsapp || receptor.telefono || '';
    const telefonoCompleto = (whatsappCountry && telefonoIngresado) 
      ? `${whatsappCountry}${telefonoIngresado}` 
      : telefonoIngresado;
    
    console.log('[DEBUG] saveReceptor - companyId obtenido:', companyId);
    
    // Verificar si es un cliente existente (tiene ID válido)
    if (receptor.id && receptor.id !== 'undefined' && receptor.id !== null) {
      try {
        const response = await authenticatedFetch(`https://nexai-billing-api.lmendoza-sv.workers.dev/api/clientes/${receptor.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            nombre: receptor.nombre,
            nit: receptor.tipoDocumento === '36' ? receptor.numDocumento : null,
            dui: receptor.tipoDocumento === '13' ? receptor.numDocumento : null,
            passport: receptor.tipoDocumento === '03' ? receptor.numDocumento : null,
            email: receptor.correoNotificacion,
            telefono: telefonoCompleto,
            direccion: receptor.direccion?.complemento,
            nrc: receptor.nrc || null,
            departamentoCode: receptor.direccion?.departamento || null,
            municipioCode: receptor.direccion?.municipio || null,
            economicActivityCode: receptor.codActividad || null,
            economicActivityDesc: receptor.descActividad || null,
          })
        });
        if (response.ok) {
          const updated = await response.json();
          return updated;
        }
      } catch (error) {
        console.error('Error actualizando cliente:', error);
      }
      return null;
    }
    
    // Es cliente nuevo - verificar si ya existe por número de documento
    // companyId ya está definido arriba
    console.log('[DEBUG] saveReceptor - companyId para búsqueda:', companyId);
    
    try {
      // Incluir companyId en la búsqueda
      const searchUrl = companyId
        ? `/api/clientes/buscar/${encodeURIComponent(receptor.numDocumento.replace(/[^0-9A-Za-z]/g, ''))}?companyId=${companyId}`
        : `https://nexai-billing-api.lmendoza-sv.workers.dev/api/clientes/buscar/${encodeURIComponent(receptor.numDocumento.replace(/[^0-9A-Za-z]/g, ''))}`;
      const searchResponse = await authenticatedFetch(searchUrl);
      if (searchResponse.ok) {
        const existingClient = await searchResponse.json();
        
        // Verificar que el cliente existente tenga un ID válido
        if (existingClient && existingClient.id && existingClient.id !== 'undefined') {
          const updateResponse = await authenticatedFetch(`https://nexai-billing-api.lmendoza-sv.workers.dev/api/clientes/${existingClient.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              nombre: receptor.nombre,
              nit: receptor.tipoDocumento === '36' ? receptor.numDocumento : (existingClient.nit || null),
              dui: receptor.tipoDocumento === '13' ? receptor.numDocumento : (existingClient.dui || null),
              passport: receptor.tipoDocumento === '03' ? receptor.numDocumento : (existingClient.passport || null),
              email: receptor.correoNotificacion || existingClient.email,
              telefono: telefonoCompleto || existingClient.phone,
              direccion: receptor.direccion?.complemento || existingClient.address,
              nrc: receptor.nrc || existingClient.nrc || null,
              departamentoCode: receptor.direccion?.departamento || existingClient.departamentoCode || null,
              municipioCode: receptor.direccion?.municipio || existingClient.municipioCode || null,
              economicActivityCode: receptor.codActividad || existingClient.codActividad || null,
              economicActivityDesc: receptor.descActividad || existingClient.descActividad || null,
            })
          });
          if (updateResponse.ok) {
            return await updateResponse.json();
          }
        }
      }
    } catch (error) {
      console.log('Cliente no encontrado, creando nuevo...');
    }
    
    // Crear nuevo cliente
    try {
      const createResponse = await authenticatedFetch('https://nexai-billing-api.lmendoza-sv.workers.dev/api/clientes/receptores', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId, // Agregar companyId al crear receptor
          legalName: receptor.nombre,
          nit: receptor.tipoDocumento === '36' ? receptor.numDocumento : null,
          dui: receptor.tipoDocumento === '13' ? receptor.numDocumento : null,
          passport: receptor.tipoDocumento === '03' ? receptor.numDocumento : null,
          otherDocType: receptor.tipoDocumento === '37' ? '37' : null,
          otherDocNumber: receptor.tipoDocumento === '37' ? receptor.numDocumento : null,
          nrc: receptor.nrc || null,
          email: receptor.correoNotificacion,
          phone: telefonoCompleto,
          address: receptor.direccion?.complemento,
          municipioCode: receptor.direccion?.municipio,
          departamentoCode: receptor.direccion?.departamento,
          economicActivityCode: receptor.codActividad,
          economicActivityDesc: receptor.descActividad,
        })
      });
      if (createResponse.ok) {
        const createdClient = await createResponse.json();
        // Si el backend detectó un duplicado y hizo merge, mostrar notificación
        if (createdClient._merged) {
          console.log('[DEBUG] Cliente existente actualizado (merge):', createdClient.id);
        }
        return createdClient;
      }
    } catch (error) {
      console.error('Error creando cliente:', error);
    }
    
    return null;
  };

  // Navegación entre pasos
  const handleNext = async () => {
    // Logging en cada paso
    if (activeStep === 0) {
      console.log('═══════════════════════════════════════════════════════════');
      console.log('STEP 1: TIPO DE DOCUMENTO SELECCIONADO');
      console.log('═══════════════════════════════════════════════════════════');
      console.log('Tipo DTE:', document.identificacion.tipoDte);
      console.log('Nombre:', DTE_TYPES.find(d => d.code === document.identificacion.tipoDte)?.name);
      console.log('JSON:', JSON.stringify({ tipoDte: document.identificacion.tipoDte }, null, 2));
    }
    
    // Si estamos en el paso de Receptor (1), mostrar el JSON del receptor
    if (activeStep === 1 && document.receptor) {
      console.log('═══════════════════════════════════════════════════════════');
      console.log('STEP 2: DATOS DEL RECEPTOR');
      console.log('═══════════════════════════════════════════════════════════');
      console.log('Tipo DTE:', document.identificacion.tipoDte);
      console.log('Receptor:', JSON.stringify(document.receptor, null, 2));
    }
    
    // Si estamos en el paso de Detalle (2), mostrar el JSON del cuerpoDocumento
    if (activeStep === 2 && document.cuerpoDocumento.length > 0) {
      console.log('═══════════════════════════════════════════════════════════');
      console.log('STEP 3: DETALLE DEL DOCUMENTO');
      console.log('═══════════════════════════════════════════════════════════');
      console.log('Tipo DTE:', document.identificacion.tipoDte);
      console.log('Cuerpo Documento:', JSON.stringify(document.cuerpoDocumento, null, 2));
    }
    
    const isValid = validateStep(activeStep);
    
    if (!isValid) {
      setErrors((prev) => ({ ...prev }));
      return;
    }
    
    // Si estamos en el paso de Receptor, guardar/actualizar cliente
    if (activeStep === 1 && document.receptor && document.receptor.nombre) {
      setIsSearchingClient(true);
      try {
        const savedReceptor = await saveReceptor();
        if (savedReceptor && savedReceptor.id) {
          // Actualizar el estado con el ID del cliente guardado
          setDocument((prev) => ({
            ...prev,
            receptor: {
              ...prev.receptor,
              id: savedReceptor.id,
            }
          }));
        }
      } catch (error) {
        console.error('Error guardando receptor:', error);
      } finally {
        setIsSearchingClient(false);
      }
    }
    
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  // Mapear tipo DTE a tipo factura para el backend
  const tipoDteToTipoFactura = {
    '01': 'CF',   // Factura Electrónica -> Consumidor Final
    '03': 'CCF',  // CCF -> Crédito Fiscal
    '04': 'NRE',  // Nota de Remisión
    '05': 'NC',   // Nota de Crédito
    '06': 'ND',   // Nota de Débito
    '07': 'RET',  // Comprobante de Retención
    '08': 'CLE',  // Comprobante de Liquidación
    '09': 'DCL',  // Documento Contable de Liquidación
    '11': 'FEX',  // Factura de Exportación
    '14': 'EXC',  // Factura Sujeto Excluido
    '15': 'CDO',  // Comprobante de Donación
  };

  // Función para firmar y enviar el DTE a MH - CORREGIDO
  const handleSignAndSend = async () => {
    if (isSubmitting || wasAcceptedByMh) return;

    setIsSubmitting(true);
    setSubmitResult(null);
    setSchemaDiagnostics([]);

    let signResult = null;

    try {
      // Limpiar el JSON de valores undefined antes de enviar
      const jsonDTE = stripEmptyOptionalFields(buildCompleteDTEJson(document, emisorData, branches));

      const schemaValidation = validateDtePayload(jsonDTE);
      setSchemaDiagnostics(schemaValidation.diagnostics || []);
      if (schemaValidation.diagnostics?.length) {
        console.warn('[DTE WIZARD] Diagnóstico AJV MH:', schemaValidation.diagnostics);
      }
      
      // Log del JSON final
      console.log('═══════════════════════════════════════════════════════════');
      console.log('DTE WIZARD: Firmando y enviando a MH');
      console.log('═══════════════════════════════════════════════════════════');
      console.log('Tipo DTE:', document.identificacion.tipoDte);
      console.log('Codigo Generacion:', jsonDTE.identificacion.codigoGeneracion);
      console.log('═══════════════════════════════════════════════════════════');

      // Validar contra esquema (server-side) antes de crear factura / firmar / enviar
      const validationResponse = await authenticatedFetch('https://nexai-billing-api.lmendoza-sv.workers.dev/api/dte/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dteJson: jsonDTE }),
      });

      const validationResult = await parseJsonResponse(validationResponse, 'No se pudo validar el DTE');
      if (!validationResponse.ok || validationResult?.success === false) {
        setSubmitResult({
          success: false,
          message: validationResult?.message || 'Validación fallida',
          observaciones: normalizeMhObservations(validationResult?.observaciones),
        });
        return;
      }

      // Primero crear la factura para obtener el documentId
      const companyId = getCurrentCompanyId();

      // Calcular montos
      const totalGravadaCalc = document.cuerpoDocumento.reduce(
        (sum, item) => {
          const cantidad = parseFloat(item.cantidad) || 1;
          const precioBase = item.precioUni != null ? parseFloat(item.precioUni) : (parseFloat(item.precioConIva) || 0) / 1.13;
          const ventaGravada = item.tipoVenta === 'GRAVADA' ? precioBase : 0;
          return sum + (ventaGravada * cantidad);
        },
        0
      );
      const totalIVACalc = (totalGravadaCalc || 0) * 0.13;
      const montoFinal = isNaN(totalGravadaCalc) ? 0 : totalGravadaCalc;
      const ivaFinal = isNaN(totalIVACalc) ? 0 : totalIVACalc;

      // Enviar al backend para crear la factura
	      const response = await authenticatedFetch('https://nexai-billing-api.lmendoza-sv.workers.dev/api/invoices', {
	        method: 'POST',
	        headers: { 'Content-Type': 'application/json' },
	        body: JSON.stringify({
	          company_id: companyId,
	          branch_id: selectedBranch?.id || null,
            pos_id: selectedPos?.id || null,
	          tipo: tipoDteToTipoFactura[document.identificacion.tipoDte] || 'CF',
          monto: montoFinal,
          iva: ivaFinal,
          retencion: 0,
          detalles: document.cuerpoDocumento.map((item, index) => {
            const cantidad = parseFloat(item.cantidad) || 1;
            const precioConIva = parseFloat(item.precioConIva) || 0;
            const precioUni = item.precioUni != null ? parseFloat(item.precioUni) : precioConIva / 1.13;
            return {
              descripcion: item.descripcion || '',
              cantidad: cantidad,
              precio: isNaN(precioUni) ? 0 : precioUni,
              total: isNaN(precioConIva * cantidad) ? 0 : precioConIva * cantidad,
              tipoVenta: item.tipoVenta || 'GRAVADA',
              uniMedida: item.uniMedida || 13,
              descuento: parseFloat(item.descuento) || 0,
            };
          }),
          json_data: jsonDTE
        })
      });

      if (!response.ok) {
        const error = await parseJsonResponse(response, 'No se pudo crear la factura');
        throw new Error(error.error || 'Error al crear la factura');
      }

	      const facturaCreada = await parseJsonResponse(response, 'No se pudo leer la factura creada');
	      const documentId = facturaCreada.id;
        const persistedDteJson = parseInvoiceJsonData(facturaCreada?.json_data);
        const finalDteJson = stripEmptyOptionalFields(mergeBackendDteIdentity(jsonDTE, persistedDteJson));

      console.log('[DTE WIZARD] Factura creada con ID:', documentId);

      // Ahora firmar y enviar a MH usando codigoGeneracion
      console.log('[DTE WIZARD] Enviando a /api/dte/sign-and-send');
      console.log('[DTE WIZARD] codigoGeneracion:', jsonDTE.identificacion.codigoGeneracion);
      console.log('[DTE WIZARD] companyId:', companyId);
      console.log('[DTE WIZARD] dteJson existe:', !!jsonDTE);
      
	      const signResponse = await authenticatedFetch('https://nexai-billing-api.lmendoza-sv.workers.dev/api/dte/sign-and-send', {
	        method: 'POST',
	        headers: { 'Content-Type': 'application/json' },
	        body: JSON.stringify({
	          documentId,
	          companyId: companyId,
	          dteJson: finalDteJson
	        })
	      });

      signResult = await parseJsonResponse(signResponse, 'No se pudo firmar y enviar el DTE');
      const selloRecepcion = getReceptionSeal(signResult);
      const observaciones = normalizeMhObservations(signResult?.observaciones);

      // El sello es la evidencia definitiva de que MH recibió el documento.
      // Puede existir junto con observaciones/advertencias y nunca debe permitirse un reenvío.
      if (selloRecepcion) {
	        setSubmitResult({
	          ...signResult,
	          success: true,
	          message: signResult.message || 'Documento firmado y enviado a MH exitosamente',
	          codigoGeneracion: finalDteJson.identificacion?.codigoGeneracion || jsonDTE.identificacion.codigoGeneracion,
	          selloRecepcion,
	          status: signResult.status || 'accepted',
              observaciones,
	        });
      } else {
        // Guardar la respuesta completa para acceder a las observaciones
        throw new Error(signResult.message || signResult.error || 'Error al firmar/enviar a MH');
      }

    } catch (error) {
      console.error('[DTE WIZARD] Error:', error);
      
      // Incluir las observaciones del MH cuando hay error de esquema
      setSubmitResult({
        success: false,
        message: error.message,
        observaciones: normalizeMhObservations(signResult?.observaciones),
        clasificaMsg: signResult?.clasificaMsg || null,
        codigoMsg: signResult?.codigoMsg || null,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Enviar documento (solo guarda localmente, sin enviar a MH) - CORREGIDO
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitResult(null);
    setSchemaDiagnostics([]);

    try {
      // Limpiar el JSON de valores undefined antes de enviar
      const jsonDTE = stripEmptyOptionalFields(buildCompleteDTEJson(document, emisorData, branches));

      const schemaValidation = validateDtePayload(jsonDTE);
      setSchemaDiagnostics(schemaValidation.diagnostics || []);
      if (schemaValidation.diagnostics?.length) {
        console.warn('[DTE WIZARD] Diagnóstico AJV MH:', schemaValidation.diagnostics);
      }
      
      // Log del JSON final
      console.log('═══════════════════════════════════════════════════════════');
      console.log('STEP 4: JSON FINAL DEL DTE');
      console.log('═══════════════════════════════════════════════════════════');
      console.log('Tipo DTE:', document.identificacion.tipoDte);
      console.log('Nombre:', DTE_TYPES.find(d => d.code === document.identificacion.tipoDte)?.name);
      console.log('JSON Completo:');
      console.log(JSON.stringify(jsonDTE, null, 2));
      console.log('═══════════════════════════════════════════════════════════');

      // Calcular montos para el backend - asegurar que sean números válidos
      const totalGravadaCalc = document.cuerpoDocumento.reduce(
        (sum, item) => {
          const cantidad = parseFloat(item.cantidad) || 1;
          // Usar precioUni si está definido, sino usar precioConIva / 1.13
          const precioBase = item.precioUni != null ? parseFloat(item.precioUni) : (parseFloat(item.precioConIva) || 0) / 1.13;
          const ventaGravada = item.tipoVenta === 'GRAVADA' ? precioBase : 0;
          return sum + (ventaGravada * cantidad);
        },
        0
      );
      const totalIVACalc = (totalGravadaCalc || 0) * 0.13;

      // Validar que los valores sean números válidos
      const montoFinal = isNaN(totalGravadaCalc) ? 0 : totalGravadaCalc;
      const ivaFinal = isNaN(totalIVACalc) ? 0 : totalIVACalc;

      // Obtener companyId del localStorage
      const companyId = getCurrentCompanyId();

      // Enviar al backend
	      const response = await authenticatedFetch('https://nexai-billing-api.lmendoza-sv.workers.dev/api/invoices', {
	        method: 'POST',
	        headers: { 'Content-Type': 'application/json' },
	        body: JSON.stringify({
	          company_id: companyId,
	          branch_id: selectedBranch?.id || null,
            pos_id: selectedPos?.id || null,
	          tipo: tipoDteToTipoFactura[document.identificacion.tipoDte] || 'CF',
          monto: montoFinal,
          iva: ivaFinal,
          retencion: 0,
          detalles: document.cuerpoDocumento.map((item, index) => {
            const cantidad = parseFloat(item.cantidad) || 1;
            const precioConIva = parseFloat(item.precioConIva) || 0;
            const precioUni = item.precioUni != null ? parseFloat(item.precioUni) : precioConIva / 1.13;
            return {
              descripcion: item.descripcion || '',
              cantidad: cantidad,
              precio: isNaN(precioUni) ? 0 : precioUni,
              total: isNaN(precioConIva * cantidad) ? 0 : precioConIva * cantidad,
              tipoVenta: item.tipoVenta || 'GRAVADA',
              uniMedida: item.uniMedida || 59,
              descuento: parseFloat(item.descuento) || 0,
            };
          }),
          json_data: jsonDTE
        })
      });

      if (response.ok) {
        const result = await parseJsonResponse(response, 'No se pudo leer el documento generado');
        setSubmitResult({
          success: true,
          message: 'Documento generado exitosamente',
          codigoGeneracion: jsonDTE.identificacion.codigoGeneracion,
        });
      } else {
        const error = await parseJsonResponse(response, 'No se pudo generar el documento');
        throw new Error(error.error || 'Error al generar el documento');
      }
    } catch (error) {
      setSubmitResult({
        success: false,
        message: 'Error al generar el documento: ' + error.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Renderizar contenido según el paso
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}>
              Seleccione el tipo de documento a emitir
            </Typography>
            <Grid container spacing={1}>
              {DTE_TYPES.map((dte) => (
                <Grid item xs={12} sm={6} key={dte.code}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border:
                        document.identificacion.tipoDte === dte.code
                          ? '2px solid #1976d2'
                          : '1px solid #ddd',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                    }}
                    onClick={() => handleTipoDteChange(dte.code)}
                  >
                    <CardContent sx={{ py: isMobile ? 1 : 2, px: isMobile ? 1 : 2 }}>
                      <Typography variant={isMobile ? 'body2' : 'subtitle1'} fontWeight="bold">
                        {dte.code} - {dte.name}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            {errors['identificacion.tipoDte'] && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errors['identificacion.tipoDte']}
              </Alert>
            )}
          </Box>
        );

      case 1: {
        const tipoDte = document.identificacion.tipoDte;
        const isNotaCreditoDebito = tipoDte === '05' || tipoDte === '06';
        const requiredFields = getReceptorRequiredFields(tipoDte);
        const isFE = tipoDte === '01';
        const isCCF = tipoDte === '03';
        const isCR = tipoDte === '07';
        const isSE = tipoDte === '14';
        // Mostrar campos adicionales para CCF, CR, CLE, DCLE (que requieren NRC, dirección, etc.)
        const requiresFullData = ['03', '05', '06', '07', '08', '09'].includes(tipoDte);

        if (isNotaCreditoDebito) {
          const relatedOptions = (recentDocs || [])
            .filter((row) => ['03', '07'].includes(String(row?.tipo_dte || '')))
            .map((row) => ({
              ...row,
              label: `${row.tipo_dte} - ${row.numero_control || ''} - ${row.receptor_name || ''} - ${row.fecha_emision || ''}`.trim(),
            }));

          const selectedRelated =
            relatedOptions.find((opt) => opt.cod_generacion === document.documentoRelacionadoBase?.codigoGeneracion) || null;

          const handleRelacionadoSelect = async (optionOrCode) => {
            const codigo = typeof optionOrCode === 'string' ? optionOrCode : optionOrCode?.cod_generacion;
            if (!codigo) return;

            try {
              const res = await authenticatedFetch(`/api/dte/document/${encodeURIComponent(codigo)}`);
              if (!res.ok) throw new Error('No se pudo obtener el documento relacionado');
              const payload = await res.json();
              const doc = payload?.documento || {};
              const info = payload?.documentoInfo || {};
              const receptor = info?.receptor || {};
              const identificacion = info?.identificacion || {};

              const relatedTipoDocumento = String(doc?.tipo_dte || identificacion?.tipoDte || '').trim();
              const relatedFechaEmision = String(doc?.fecha_emision || identificacion?.fecEmi || '').trim();
              if (!['03', '07'].includes(relatedTipoDocumento)) {
                throw new Error(`El documento relacionado debe ser 03 (CCF) o 07 (CR). Recibido: ${relatedTipoDocumento || 'N/D'}`);
              }

              const receptorNitDigits =
                String(receptor?.nit || '')
                  .replace(/[^0-9]/g, '')
                  .slice(0, 14) ||
                (String(receptor?.tipoDocumento || '') === '36'
                  ? String(receptor?.numDocumento || '').replace(/[^0-9]/g, '').slice(0, 14)
                  : '');

              const formattedNit = receptorNitDigits ? DOCUMENT_FORMATTERS['36'](receptorNitDigits) : '';

              setDocument((prev) => ({
                ...prev,
                documentoRelacionadoBase: {
                  tipoDocumento: relatedTipoDocumento,
                  codigoGeneracion: codigo,
                  fechaEmision: relatedFechaEmision,
                },
                receptor: {
                  tipoDocumento: '36',
                  numDocumento: formattedNit,
                  nombre: receptor?.nombre || doc?.receptor_name || '',
                  nombreComercial: receptor?.nombreComercial || receptor?.nombre || '',
                  nrc: receptor?.nrc || doc?.receptor_nrc || '',
                  correoNotificacion: receptor?.correo || '',
                  telefono: receptor?.telefono || '',
                  direccion: receptor?.direccion
                    ? {
                        departamento: receptor?.direccion?.departamento || '',
                        municipio: receptor?.direccion?.municipio || '',
                        complemento: receptor?.direccion?.complemento || '',
                      }
                    : null,
                  codActividad: receptor?.codActividad || '',
                  descActividad: receptor?.descActividad || '',
                },
              }));
            } catch (e) {
              console.error('[DTEWizard] Error cargando documento relacionado:', e);
              setErrors((prev) => ({
                ...prev,
                documentoRelacionado: 'No se pudo cargar el documento relacionado. Verifique el código de generación.',
              }));
            }
          };

          return (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}>
                Documento relacionado (requerido)
              </Typography>

              <Grid container spacing={isMobile ? 1 : 2}>
                <Grid item xs={12} md={8}>
                  <Autocomplete
                    options={relatedOptions}
                    loading={isLoadingRecentDocs}
                    value={selectedRelated}
                    onChange={(_, value) => handleRelacionadoSelect(value)}
                    getOptionLabel={(option) => option?.label || option?.cod_generacion || ''}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Seleccione documento (CCF/CR) para relacionar"
                        size={isMobile ? 'small' : 'medium'}
                        error={!!errors['documentoRelacionado']}
                        helperText={errors['documentoRelacionado']}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoadingRecentDocs ? <CircularProgress color="inherit" size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size={isMobile ? 'small' : 'medium'}
                    label="O ingrese Código de Generación"
                    value={document.documentoRelacionadoBase?.codigoGeneracion || ''}
                    onChange={(e) =>
                      setDocument((prev) => ({
                        ...prev,
                        documentoRelacionadoBase: {
                          ...(prev.documentoRelacionadoBase || {}),
                          codigoGeneracion: (e.target.value || '').toUpperCase(),
                        },
                      }))
                    }
                    onBlur={(e) => {
                      const value = (e.target.value || '').trim();
                      if (value) handleRelacionadoSelect(value);
                    }}
                  />
                </Grid>

                {document.documentoRelacionadoBase?.codigoGeneracion && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      El receptor y la referencia se cargan desde el documento relacionado.
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Box>
          );
        }
        
        // Estado de campos para mostrar/ocultar de forma progresiva
        const hasTipoDocumento = !!document.receptor?.tipoDocumento;
        const hasNumDocumento = !!document.receptor?.numDocumento;
        const hasNombre = !!document.receptor?.nombre;
        const hasNRC = isCCF || isCR ? !!document.receptor?.nrc : (!!document.receptor?.nrc && clienteMode === 'existente');
        const hasActividad = isCCF || isCR ? !!document.receptor?.codActividad : (!!document.receptor?.codActividad && clienteMode === 'existente');
        const hasDepartamento = isCCF || isCR ? !!document.receptor?.direccion?.departamento : (!!document.receptor?.direccion?.departamento && clienteMode === 'existente');
        const hasMunicipio = isCCF || isCR ? !!document.receptor?.direccion?.municipio : (!!document.receptor?.direccion?.municipio && clienteMode === 'existente');
        const hasDireccion = isCCF || isCR ? !!document.receptor?.direccion?.complemento : (!!document.receptor?.direccion?.complemento && clienteMode === 'existente');
        
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}>
              Datos del Receptor
            </Typography>
            
            {/* Toggle Cliente Existente / Nuevo */}
            <ToggleButtonGroup
              value={clienteMode}
              exclusive
              onChange={(e, value) => value && handleClienteModeChange(value)}
              sx={{ mb: 2, width: '100%' }}
              fullWidth
            >
              <ToggleButton value="existente">Cliente Existente</ToggleButton>
              <ToggleButton value="nuevo">Cliente Nuevo</ToggleButton>
            </ToggleButtonGroup>
            
            <Grid container spacing={isMobile ? 1 : 2}>
              {/* Paso 1: Tipo de Documento - siempre visible primero */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors['receptor.tipoDocumento']}>
                  <InputLabel>Tipo de Documento</InputLabel>
                  <Select
                    value={document.receptor?.tipoDocumento || ''}
                    label="Tipo de Documento"
                    onChange={(e) =>
                      setDocument((prev) => ({
                        ...prev,
                        receptor: { 
                          ...prev.receptor, 
                          tipoDocumento: e.target.value, 
                          numDocumento: '',
                          nombre: '' 
                        },
                      }))
                    }
                  >
                    <MenuItem value="36">36 - NIT</MenuItem>
                    <MenuItem value="13">13 - DUI</MenuItem>
                    <MenuItem value="02">02 - Carnet Residente</MenuItem>
                    <MenuItem value="03">03 - Pasaporte</MenuItem>
                    <MenuItem value="37">37 - Otro</MenuItem>
                  </Select>
                  {errors['receptor.tipoDocumento'] && (
                    <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                      {errors['receptor.tipoDocumento']}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              
              {/* Paso 2: Número de Documento - mismo componente para todos los tipos */}
              {hasTipoDocumento && (
                <Grid item xs={12} sm={6}>
                  <ReceptorSearch
                    tipoDocumento={document.receptor?.tipoDocumento || ''}
                    numDocumento={document.receptor?.numDocumento || ''}
                    receptor={document.receptor}
                    onSelect={handleReceptorSelect}
                    companyId={getCurrentCompanyId()}
                    onInputChange={(value) => {
                      setDocument((prev) => ({
                        ...prev,
                        receptor: { ...prev.receptor, numDocumento: value },
                      }));
                    }}
                    label="Buscar / Ingresar Número"
                    required={true}
                    error={errors['receptor.numDocumento'] || ''}
                    disabled={false}
                  />
                </Grid>
              )}
              
              {/* Paso 3: Nombre - aparece cuando hay número de documento */}
              {hasNumDocumento && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size={isMobile ? 'small' : 'medium'}
                    label="Nombre / Razón Social"
                    value={document.receptor?.nombre || ''}
                    onChange={(e) =>
                      setDocument((prev) => ({
                        ...prev,
                        receptor: { ...prev.receptor, nombre: e.target.value },
                      }))
                    }
                    error={!!errors['receptor.nombre']}
                    helperText={errors['receptor.nombre']}
                    required
                  />
                </Grid>
              )}
              
              {/* Campos adicionales para Cliente Nuevo - siempre visibles en modo nuevo */}
              {clienteMode === 'nuevo' && hasNombre && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>
                        Datos del Cliente
                      </Typography>
                      {requiresFullData && (
                        <Chip 
                          label="Los campos marcados son requeridos para CCF y CR" 
                          size="small" 
                          color="warning" 
                          variant="outlined" 
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  </Grid>
                  
                  {/* NRC - más pequeño */}
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size={isMobile ? 'small' : 'medium'}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          NRC
                          {requiresFullData && <Chip label="Requerido" size="small" color="error" sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }} />}
                        </Box>
                      }
                      value={document.receptor?.nrc || ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setDocument((prev) => ({
                          ...prev,
                          receptor: { ...prev.receptor, nrc: value },
                        }));
                      }}
                      error={!!errors['receptor.nrc']}
                      helperText={errors['receptor.nrc']}
                      inputProps={{ maxLength: 8 }}
                    />
                  </Grid>
                  
                  {/* Código de Actividad - más grande, toda la fila */}
                  <Grid item xs={12} sm={8}>
                    <EconomicActivitySelect
                      value={document.receptor?.codActividad || ''}
                      onChange={(value) =>
                        setDocument((prev) => ({
                          ...prev,
                          receptor: { ...prev.receptor, codActividad: value },
                        }))
                      }
                      onSelect={(option) =>
                        setDocument((prev) => ({
                          ...prev,
                          receptor: { 
                            ...prev.receptor, 
                            codActividad: option?.code || '',
                            descActividad: option?.name || '' 
                          },
                        }))
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Actividad Económica
                          {requiresFullData && <Chip label="Requerido" size="small" color="error" sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }} />}
                        </Box>
                      }
                      error={errors['receptor.codActividad'] || ''}
                    />
                  </Grid>
                  
                  {/* Departamento - como Select */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                      <InputLabel>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Departamento
                          {requiresFullData && <Chip label="Requerido" size="small" color="error" sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }} />}
                        </Box>
                      </InputLabel>
                      <Select
                        value={document.receptor?.direccion?.departamento || ''}
                        label="Departamento"
                        onChange={(e) =>
                          setDocument((prev) => ({
                            ...prev,
                            receptor: {
                              ...prev.receptor,
                              direccion: {
                                ...prev.receptor?.direccion,
                                departamento: e.target.value,
                                municipio: '',
                                complemento: ''
                              }
                            },
                          }))
                        }
                      >
                        {catalogs.departamentos.map((dept) => (
                          <MenuItem key={dept.codigo} value={dept.codigo}>
                            {dept.nombre}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {/* Municipio - como Select */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size={isMobile ? 'small' : 'medium'} disabled={!document.receptor?.direccion?.departamento}>
                      <InputLabel>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Municipio
                          {requiresFullData && <Chip label="Requerido" size="small" color="error" sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }} />}
                        </Box>
                      </InputLabel>
                      <Select
                        value={document.receptor?.direccion?.municipio || ''}
                        label="Municipio"
                        onChange={(e) =>
                          setDocument((prev) => ({
                            ...prev,
                            receptor: {
                              ...prev.receptor,
                              direccion: {
                                ...prev.receptor?.direccion,
                                municipio: e.target.value
                              }
                            },
                          }))
                        }
                      >
                        {filteredMunicipios.map((mun) => (
                          <MenuItem key={mun.codigo} value={mun.codigo}>
                            {mun.nombre}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {/* Dirección - full width */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size={isMobile ? 'small' : 'medium'}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Dirección
                          {requiresFullData && <Chip label="Requerido" size="small" color="error" sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }} />}
                        </Box>
                      }
                      value={document.receptor?.direccion?.complemento || ''}
                      onChange={(e) =>
                        setDocument((prev) => ({
                          ...prev,
                          receptor: {
                            ...prev.receptor,
                            direccion: {
                              ...prev.receptor?.direccion,
                              complemento: e.target.value
                            }
                          },
                        }))
                      }
                      error={!!errors['receptor.direccion.complemento']}
                      helperText={errors['receptor.direccion.complemento']}
                    />
                  </Grid>
                </>
              )}
              
              {/* Campos adicionales para CCF y CR */}
              {requiresFullData && hasNombre && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>
                    Datos de Contribuyente
                  </Typography>
                </Grid>
              )}
              
              {/* Paso 4: NRC - aparece cuando hay nombre (para CCF/CR) */}
              {requiresFullData && hasNombre && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size={isMobile ? 'small' : 'medium'}
                    label="NRC"
                    value={document.receptor?.nrc || ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setDocument((prev) => ({
                        ...prev,
                        receptor: { ...prev.receptor, nrc: value },
                      }));
                    }}
                    error={!!errors['receptor.nrc']}
                    helperText={errors['receptor.nrc'] || 'NRC del receptor'}
                    inputProps={{ maxLength: 8 }}
                    required
                  />
                </Grid>
              )}
              
              {/* Paso 5: Correo - aparece cuando hay NRC (para CCF/CR) */}
              {requiresFullData && hasNRC && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size={isMobile ? 'small' : 'medium'}
                    label="Correo Electrónico"
                    value={document.receptor?.correoNotificacion || ''}
                    onChange={(e) =>
                      setDocument((prev) => ({
                        ...prev,
                        receptor: { ...prev.receptor, correoNotificacion: e.target.value },
                      }))
                    }
                    error={!!errors['receptor.correoNotificacion']}
                    helperText={errors['receptor.correoNotificacion']}
                    required
                  />
                </Grid>
              )}
              
              {/* Teléfono - opcional para CCF/CR */}
              {requiresFullData && hasNRC && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size={isMobile ? 'small' : 'medium'}
                    label="Teléfono"
                    value={document.receptor?.telefono || ''}
                    onChange={(e) =>
                      setDocument((prev) => ({
                        ...prev,
                        receptor: { ...prev.receptor, telefono: e.target.value },
                      }))
                    }
                  />
                </Grid>
              )}
              
              {/* Paso 6: Actividad Económica - aparece cuando hay NRC (para CCF/CR) */}
              {requiresFullData && hasNRC && (
                <Grid item xs={12}>
                  <EconomicActivitySelect
                    value={document.receptor?.codActividad || ''}
                    onChange={(value) =>
                      setDocument((prev) => ({
                        ...prev,
                        receptor: { ...prev.receptor, codActividad: value },
                      }))
                    }
                    onSelect={(option) =>
                      setDocument((prev) => ({
                        ...prev,
                        receptor: { 
                          ...prev.receptor, 
                          codActividad: option?.code || '',
                          descActividad: option?.name || '' 
                        },
                      }))
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Actividad Económica
                        {requiresFullData && <Chip label="Requerido" size="small" color="error" sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }} />}
                      </Box>
                    }
                    error={errors['receptor.codActividad'] || ''}
                  />
                </Grid>
              )}
              
              {/* Paso 7: Departamento - aparece cuando hay actividad (para CCF/CR) */}
              {requiresFullData && hasActividad && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                    <InputLabel>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Departamento
                        {requiresFullData && <Chip label="Requerido" size="small" color="error" sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }} />}
                      </Box>
                    </InputLabel>
                    <Select
                      value={document.receptor?.direccion?.departamento || ''}
                      label="Departamento"
                      onChange={(e) =>
                        setDocument((prev) => ({
                          ...prev,
                          receptor: {
                            ...prev.receptor,
                            direccion: {
                              ...prev.receptor?.direccion,
                              departamento: e.target.value,
                              municipio: '',
                              distrito: '',
                              complemento: ''
                            }
                          },
                        }))
                      }
                    >
                      {catalogs.departamentos.map((dept) => (
                        <MenuItem key={dept.codigo} value={dept.codigo}>
                          {dept.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              
              {/* Paso 8: Municipio - aparece cuando hay departamento (para CCF/CR) */}
              {requiresFullData && hasDepartamento && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size={isMobile ? 'small' : 'medium'} disabled={!document.receptor?.direccion?.departamento}>
                    <InputLabel>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Municipio
                        {requiresFullData && <Chip label="Requerido" size="small" color="error" sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }} />}
                      </Box>
                    </InputLabel>
                    <Select
                      value={document.receptor?.direccion?.municipio || ''}
                      label="Municipio"
                      onChange={(e) =>
                        setDocument((prev) => ({
                          ...prev,
                          receptor: {
                            ...prev.receptor,
                            direccion: {
                              ...prev.receptor?.direccion,
                              municipio: e.target.value
                            }
                          },
                        }))
                      }
                    >
                      {filteredMunicipios.map((mun) => (
                        <MenuItem key={mun.codigo} value={mun.codigo}>
                          {mun.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              
              {/* Paso 9: Dirección - aparece cuando hay municipio (para CCF/CR) */}
              {requiresFullData && hasMunicipio && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size={isMobile ? 'small' : 'medium'}
                    label="Dirección"
                    value={document.receptor?.direccion?.complemento || ''}
                    onChange={(e) =>
                      setDocument((prev) => ({
                        ...prev,
                        receptor: {
                          ...prev.receptor,
                          direccion: {
                            ...prev.receptor?.direccion,
                            complemento: e.target.value
                          }
                        },
                      }))
                    }
                    error={!!errors['receptor.direccion.complemento']}
                    helperText={errors['receptor.direccion.complemento']}
                    required
                  />
                </Grid>
              )}

              {requiresFullData && hasMunicipio && (
                <Grid item xs={12} sm={6}>
                  <DistrictSelect
                    value={document.receptor?.direccion?.distrito || ''}
                    onChange={(value) =>
                      setDocument((prev) => ({
                        ...prev,
                        receptor: {
                          ...prev.receptor,
                          direccion: { ...prev.receptor?.direccion, distrito: value }
                        },
                      }))
                    }
                    size={isMobile ? 'small' : 'medium'}
                    label="Distrito"
                    error={!!errors['receptor.direccion.distrito']}
                    required
                  />
                </Grid>
              )}
              
              {/* Contacto para FE y SE - opcional */}
              {(isFE || isSE) && hasNombre && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>
                      Contacto para recibir el DTE (Opcional)
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size={isMobile ? 'small' : 'medium'}
                      label="Correo para recibir DTE"
                      value={document.receptor?.correoNotificacion || ''}
                      onChange={(e) =>
                        setDocument((prev) => ({
                          ...prev,
                          receptor: { ...prev.receptor, correoNotificacion: e.target.value },
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', gap: 1, flexDirection: isMobile ? 'column' : 'row' }}>
                      <FormControl sx={{ minWidth: isMobile ? 70 : 85 }} size={isMobile ? 'small' : 'medium'}>
                        <InputLabel>País</InputLabel>
                        <Select
                          value={whatsappCountry}
                          label="País"
                          onChange={(e) => setWhatsappCountry(e.target.value)}
                        >
                          {COUNTRIES.map((country) => (
                            <MenuItem key={country.code} value={country.prefix}>
                              +{country.prefix}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        label="WhatsApp"
                        value={document.receptor?.whatsapp || ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 8);
                          setDocument((prev) => ({
                            ...prev,
                            receptor: { ...prev.receptor, whatsapp: value },
                          }));
                        }}
                        inputProps={{ maxLength: 8 }}
                      />
                    </Box>
                  </Grid>
                </>
              )}
              
              {errors['receptor'] && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    {errors['receptor']}
                  </Alert>
                </Grid>
              )}
              
              {/* Mostrar TODOS los errores de validación, incluyendo campos ocultos */}
              {Object.keys(errors).filter(key => key.startsWith('receptor.')).length > 0 && (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Complete los siguientes campos requeridos:
                    </Typography>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      {errors['receptor.tipoDocumento'] && (
                        <li>Tipo de Documento</li>
                      )}
                      {errors['receptor.numDocumento'] && (
                        <li>Número de Documento</li>
                      )}
                      {errors['receptor.nombre'] && (
                        <li>Nombre o Razón Social</li>
                      )}
                      {errors['receptor.nrc'] && (
                        <li>NRC</li>
                      )}
                      {errors['receptor.correoNotificacion'] && (
                        <li>Correo Electrónico</li>
                      )}
                      {errors['receptor.codActividad'] && (
                        <li>Actividad Económica</li>
                      )}
                      {errors['receptor.direccion.departamento'] && (
                        <li>Departamento</li>
                      )}
                      {errors['receptor.direccion.municipio'] && (
                        <li>Municipio</li>
                      )}
                      {errors['receptor.direccion.complemento'] && (
                        <li>Dirección</li>
                      )}
                    </ul>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Box>
        );
      }

      case 2:
        // Verificar si el documento tiene referencias a otros documentos (CR)
        const isCRDocument = hasDocumentReference(document.identificacion.tipoDte);
        
        // Si es un documento con referencia (CR), renderizar el componente especializado
        if (isCRDocument) {
          return (
            <StepDetailReferencia
              document={document}
              setDocument={setDocument}
              handleAddItem={handleAddItem}
              handleRemoveItem={handleRemoveItem}
              handleItemChange={handleItemChange}
              handleHeaderChange={handleHeaderChange}
              expandedItems={expandedItems}
              toggleItemAdvanced={toggleItemAdvanced}
              errors={errors}
              isMobile={isMobile}
              branches={branches}
              expandedSections={expandedSections}
              setExpandedSections={setExpandedSections}
            />
          );
        }
        
        // Calcular totales según el tipo de documento
        const isCCF = document.identificacion.tipoDte === '03';
        const isFSE = document.identificacion.tipoDte === '14';
        
        // Calcular subtotal (sin IVA para CCF, con IVA para otros)
        const calcularSubtotal = () => {
          return document.cuerpoDocumento.reduce((sum, item) => {
            const cantidad = parseFloat(item.cantidad) || 1;
            if (isCCF) {
              // Para CCF: precio sin IVA
              const precioSinIva = parseFloat(item.precioUni) || 0;
              const montoDescu = parseFloat(item.montoDescu) || 0;
              return sum + ((precioSinIva - montoDescu) * cantidad);
            } else {
              // Para FCF/FSE: precio con IVA
              const precioConIva = parseFloat(item.precioConIva) || 0;
              const montoDescu = parseFloat(item.montoDescu) || 0;
              return sum + ((precioConIva - montoDescu) * cantidad);
            }
          }, 0);
        };
        
        const subtotal = calcularSubtotal();
        
        // Calcular IVA (solo para CCF: subtotal * 0.13)
        const ivaCalculado = isCCF ? subtotal * 0.13 : 0;
        
        // Calcular descuento total
        const descuentoTotal = document.cuerpoDocumento.reduce((sum, item) => {
          const cantidad = parseFloat(item.cantidad) || 1;
          const montoDescu = parseFloat(item.montoDescu) || 0;
          return sum + (montoDescu * cantidad);
        }, 0);
        
        // Calcular retención de renta (solo para FSE: 10%)
        const retencionRenta = isFSE ? subtotal * 0.10 : 0;
        
        // Calcular total a pagar
        const totalPagar = isFSE ? subtotal - retencionRenta : (isCCF ? subtotal + ivaCalculado : subtotal);
        
        return (
          <Box sx={{ mt: 2, overflowX: 'auto' }}>
            {/* Accordion de Datos del Documento (Header) - Collapsed por defecto */}
            <Accordion 
              expanded={expandedSections.header} 
              onChange={() => setExpandedSections(prev => ({ ...prev, header: !prev.header }))}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Datos del Documento
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {/* Fecha de Emisión */}
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Fecha de Emisión"
                      type="date"
                      value={document.header?.fechaEmision || new Date().toISOString().split('T')[0]}
                      onChange={(e) => handleHeaderChange('fechaEmision', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        max: new Date().toISOString().split('T')[0]
                      }}
                    />
                  </Grid>

                  {/* Forma de Pago */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Forma de Pago</InputLabel>
                      <Select
                        value={document.header?.formaPago || 'contado'}
                        label="Forma de Pago"
                        onChange={(e) => handleHeaderChange('formaPago', e.target.value)}
                      >
                        {FORMAS_PAGO.map((fp) => (
                          <MenuItem key={fp.code} value={fp.code}>
                            {fp.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Si es crédito, mostrar campos adicionales */}
                  {document.header?.formaPago === 'credito' && (
                    <>
                      {/* Plazo */}
                      <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Plazo</InputLabel>
                          <Select
                            value={document.header?.plazo || 'dias'}
                            label="Plazo"
                            onChange={(e) => handleHeaderChange('plazo', e.target.value)}
                          >
                            {PLAZOS_CREDITO.map((p) => (
                              <MenuItem key={p.code} value={p.code}>
                                {p.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Cantidad de plazo */}
                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Cantidad"
                          type="number"
                          value={document.header?.cantidadPlazo || 30}
                          onChange={(e) => handleHeaderChange('cantidadPlazo', parseInt(e.target.value) || 30)}
                        />
                      </Grid>

                      {/* Método de Pago */}
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Método de Pago</InputLabel>
                          <Select
                            value={document.header?.metodoPago || '01'}
                            label="Método de Pago"
                            onChange={(e) => handleHeaderChange('metodoPago', e.target.value)}
                          >
                            {METODOS_PAGO.map((mp) => (
                              <MenuItem key={mp.code} value={mp.code}>
                                {mp.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </>
                  )}

                  {/* Si es contado, mostrar solo método de pago */}
                  {document.header?.formaPago !== 'credito' && (
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Método de Pago</InputLabel>
                        <Select
                          value={document.header?.metodoPago || '01'}
                          label="Método de Pago"
                          onChange={(e) => handleHeaderChange('metodoPago', e.target.value)}
                        >
                          {METODOS_PAGO.map((mp) => (
                            <MenuItem key={mp.code} value={mp.code}>
                              {mp.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}

                  {/* Sucursal */}
	                  <Grid item xs={12} sm={6} md={3}>
	                    <FormControl fullWidth size="small">
	                      <InputLabel>Sucursal</InputLabel>
	                      <Select
	                        value={document.header?.sucursal || ''}
                        label="Sucursal"
                        onChange={(e) => handleHeaderChange('sucursal', e.target.value)}
                      >
                        {branches.length > 0 ? (
                          branches.map((branch) => (
                            <MenuItem key={branch.id} value={branch.codigo_establecimiento}>
                              {branch.branch_name} ({branch.codigo_establecimiento})
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem value="0">Matriz</MenuItem>
                        )}
	                      </Select>
	                    </FormControl>
	                  </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small" error={!!errors['header.posId']} disabled={!selectedBranch || !availablePointsOfSale.length}>
                        <InputLabel>Caja</InputLabel>
                        <Select
                          value={document.header?.posId || ''}
                          label="Caja"
                          onChange={(e) => {
                            const companyId = getCurrentCompanyId();
                            const posId = e.target.value;
                            const pos = availablePointsOfSale.find((item) => item.id === posId) || null;

                            setDocument((prev) => ({
                              ...prev,
                              header: {
                                ...prev.header,
                                posId,
                                puntoVenta: pos?.codigo_punto_venta || '',
                              },
                            }));

                            if (selectedBranch?.codigo_establecimiento && posId) {
                              persistSelectedPos(companyId, selectedBranch.codigo_establecimiento, posId);
                            }
                          }}
                        >
                          {availablePointsOfSale.map((pos) => (
                            <MenuItem key={pos.id} value={pos.id}>
                              {pos.pos_name} ({normalizePosValue(pos.codigo_punto_venta)})
                            </MenuItem>
                          ))}
                        </Select>
                        {errors['header.posId'] && <FormHelperText>{errors['header.posId']}</FormHelperText>}
                      </FormControl>
                    </Grid>
	                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Accordion de Detalle del Documento - Expanded por defecto */}
            <Accordion 
              expanded={expandedSections.detalle} 
              onChange={() => setExpandedSections(prev => ({ ...prev, detalle: !prev.detalle }))}
              defaultExpanded
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Detalle del Documento
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {/* Título y botón agregar */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}></Typography>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddItem} size={isMobile ? 'small' : 'medium'}>
                    Agregar producto
                  </Button>
                </Box>

                {errors['cuerpoDocumento'] && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errors['cuerpoDocumento']}
                  </Alert>
                )}

                {document.cuerpoDocumento.length > 0 ? (
                  document.cuerpoDocumento.map((item, index) => (
                    <Card key={index} sx={{ mb: 2, p: 1 }}>
                      {/* Fila 1: Campos obligatorios */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        <Typography variant="body2" sx={{ minWidth: 30 }}>#{index + 1}</Typography>
                        
                        {/* Descripción - Obligatorio */}
                        <TextField
                          size="small"
                          label="Descripción *"
                          value={item.descripcion}
                          onChange={(e) => handleItemChange(index, 'descripcion', e.target.value)}
                          sx={{ minWidth: isMobile ? 150 : 250, flex: 1 }}
                          error={!!errors[`cuerpoDocumento.${index}.descripcion`]}
                          helperText={errors[`cuerpoDocumento.${index}.descripcion`]}
                        />
                        
                        {/* Precio con IVA - Obligatorio */}
                        <TextField
                          size="small"
                          label="Precio (Con IVA) *"
                          type="number"
                          value={item.precioConIva === 0 ? '' : item.precioConIva}
                          onKeyDown={(e) => {
                            const validKeys = /^[0-9.]$/;
                            if (!validKeys.test(e.key) && 
                                !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e) => {
                            const value = e.target.value;
                            const filtered = value.replace(/[^0-9.]/g, '');
                            const parts = filtered.split('.');
                            let finalValue = parts[0];
                            if (parts.length > 1) {
                              finalValue += '.' + parts.slice(1).join('');
                            }
                            
                            if (finalValue === '' || finalValue === '.') {
                              handleItemChange(index, 'precioConIva', 0);
                              handleItemChange(index, 'precioUni', 0);
                              handleItemChange(index, 'ventaGravada', 0);
                            } else {
                              const precioConIva = parseFloat(finalValue) || 0;
                              const precioSinIva = precioConIva / 1.13;
                              handleItemChange(index, 'precioConIva', precioConIva);
                              handleItemChange(index, 'precioUni', precioSinIva);
                              handleItemChange(index, 'ventaGravada', precioSinIva);
                            }
                          }}
                          sx={{ minWidth: isMobile ? 100 : 130 }}
                          error={!!errors[`cuerpoDocumento.${index}.precioConIva`]}
                          helperText={errors[`cuerpoDocumento.${index}.precioConIva`]}
                        />

                        {/* Botón eliminar */}
                        <IconButton color="error" size="small" onClick={() => handleRemoveItem(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>

                      {/* Fila 2: Campos parametriazbles (siempre visibles) */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {/* Tipo: Bien, Servicio, Ambos */}
                        <FormControl size="small" sx={{ minWidth: isMobile ? 70 : 100 }}>
                          <InputLabel>Tipo</InputLabel>
                          <Select
                            value={item.tipoItem || 1}
                            label="Tipo"
                            onChange={(e) => handleItemChange(index, 'tipoItem', e.target.value)}
                          >
                            {TIPOS_ITEM.map((t) => (
                              <MenuItem key={t.code} value={t.code}>
                                {t.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Tipo de Venta: Gravada, Exenta, No Sujeta */}
                        <FormControl size="small" sx={{ minWidth: isMobile ? 80 : 120 }}>
                          <InputLabel>Tipo Venta</InputLabel>
                          <Select
                            value={item.tipoVenta || 'GRAVADA'}
                            label="Tipo Venta"
                            onChange={(e) => handleItemChange(index, 'tipoVenta', e.target.value)}
                          >
                            {TIPOS_VENTA.map((tv) => (
                              <MenuItem key={tv.code} value={tv.code}>
                                {tv.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Cantidad */}
                        <TextField
                          size="small"
                          label="Cantidad"
                          type="number"
                          value={item.cantidad == null ? '' : item.cantidad}
                          onChange={(e) => {
                            const val = e.target.value;
                            const filtered = val.replace(/[^0-9.]/g, '');
                            if (filtered === '' || filtered === '.') {
                              handleItemChange(index, 'cantidad', '');
                            } else {
                              handleItemChange(index, 'cantidad', filtered);
                            }
                          }}
                          sx={{ width: isMobile ? 70 : 80 }}
                        />

                        {/* Unidad */}
                        <FormControl size="small" sx={{ minWidth: isMobile ? 70 : 100 }}>
                          <InputLabel>Unidad</InputLabel>
                          <Select
                            value={item.uniMedida || 13}
                            label="Unidad"
                            onChange={(e) => handleItemChange(index, 'uniMedida', e.target.value)}
                          >
                            {UNIDADES_MEDIDA.map((u) => (
                              <MenuItem key={u.code} value={u.code}>
                                {u.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* % de Descuento */}
                        <TextField
                          size="small"
                          label="% Desc."
                          type="number"
                          value={item.descuento === 0 ? '' : item.descuento}
                          onKeyDown={(e) => {
                            const validKeys = /^[0-9.]$/;
                            if (!validKeys.test(e.key) && 
                                !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e) => {
                            const value = e.target.value;
                            const filtered = value.replace(/[^0-9.]/g, '');
                            const parts = filtered.split('.');
                            let finalValue = parts[0];
                            if (parts.length > 1) {
                              finalValue += '.' + parts.slice(1).join('');
                            }
                            
                            if (finalValue === '' || finalValue === '.') {
                              handleItemChange(index, 'descuento', 0);
                              handleItemChange(index, 'montoDescu', 0);
                            } else {
                              const descuento = parseFloat(finalValue) || 0;
                              handleItemChange(index, 'descuento', descuento);
                              const montoDescu = (item.precioConIva * descuento) / 100;
                              handleItemChange(index, 'montoDescu', montoDescu);
                            }
                          }}
                          sx={{ width: isMobile ? 60 : 80 }}
                        />
                      </Box>
                    </Card>
                  ))
                ) : (
                  <Alert severity="info">No hay items agregados. Haga clic en "Agregar producto" para comenzar.</Alert>
                )}

                {/* Sección de Totales */}
                {document.cuerpoDocumento.length > 0 && (
                  <Card sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5' }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Resumen de Totales
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="body2">SUMAS</Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'right' }}>
                        <Typography variant="body2">
                          ${subtotal.toFixed(2)}
                        </Typography>
                      </Grid>

                      {isCCF && (
                        <>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="error">IVA (13%)</Typography>
                          </Grid>
                          <Grid item xs={6} sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" color="error">${ivaCalculado.toFixed(2)}</Typography>
                          </Grid>
                        </>
                      )}

                      {!isCCF && (
                        <>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="error">(-) IVA RETENIDO</Typography>
                          </Grid>
                          <Grid item xs={6} sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" color="error">$0.00</Typography>
                          </Grid>
                        </>
                      )}

                      {isFSE && (
                        <>
                          <Grid item xs={6}>
                            <Typography variant="body2">RENTA 10% (ISR)</Typography>
                          </Grid>
                          <Grid item xs={6} sx={{ textAlign: 'right' }}>
                            <Typography variant="body2">-${retencionRenta.toFixed(2)}</Typography>
                          </Grid>
                        </>
                      )}

                      {!isFSE && (
                        <>
                          <Grid item xs={6}>
                            <Typography variant="body2">RENTA 10% (ISR)</Typography>
                          </Grid>
                          <Grid item xs={6} sx={{ textAlign: 'right' }}>
                            <Typography variant="body2">$0.00</Typography>
                          </Grid>
                        </>
                      )}

                      <Grid item xs={6}>
                        <Typography variant="body2" color="primary">DESCUENTO</Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" color="primary">
                          -${descuentoTotal.toFixed(2)}
                        </Typography>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="body2" fontWeight="bold">SUB TOTAL</Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" fontWeight="bold">
                          ${subtotal.toFixed(2)}
                        </Typography>
                      </Grid>

                      {!isCCF && !isFSE && (
                        <>
                          <Grid item xs={6}>
                            <Typography variant="body2">VENTAS NO SUJETA</Typography>
                          </Grid>
                          <Grid item xs={6} sx={{ textAlign: 'right' }}>
                            <Typography variant="body2">$0.00</Typography>
                          </Grid>

                          <Grid item xs={6}>
                            <Typography variant="body2">VENTAS EXENTAS</Typography>
                          </Grid>
                          <Grid item xs={6} sx={{ textAlign: 'right' }}>
                            <Typography variant="body2">$0.00</Typography>
                          </Grid>
                        </>
                      )}

                      <Grid item xs={6}>
                        <Typography variant="body2">PROPINA</Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'right' }}>
                        <Typography variant="body2">$0.00</Typography>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="subtitle1" fontWeight="bold">TOTAL Documento</Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'right' }}>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">
                          ${totalPagar.toFixed(2)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Card>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
        );

      case 3: {
        const tipoDte = document.identificacion.tipoDte;
        const isCR = tipoDte === '07';
        
        // Para CR (Comprobante de Retención), usar montoSujetoGrav e ivaRetenido
        // Para otros tipos, usar ventaGravada e ivaItem
        let totalGravada, totalIVA, total;
        
        if (isCR) {
          // CR: usar campos específicos de retención
          totalGravada = document.cuerpoDocumento.reduce(
            (sum, item) => sum + (parseFloat(item.montoSujetoGrav) || 0),
            0
          );
          totalIVA = document.cuerpoDocumento.reduce(
            (sum, item) => sum + (parseFloat(item.ivaRetenido) || 0),
            0
          );
          total = totalGravada; // Para CR, el total es el monto sujeto a retención
        } else {
          // Otros tipos: FE, CCF, etc.
          totalGravada = document.cuerpoDocumento.reduce(
            (sum, item) => sum + (item.ventaGravada || 0),
            0
          );
          totalIVA = totalGravada * 0.13;
          total = totalGravada + totalIVA;
        }

        // Obtener nombres de los catálogos
        const formaPagoNombre = FORMAS_PAGO.find(f => f.code === document.header?.formaPago)?.name || 'Contado';
        const metodoPagoNombre = METODOS_PAGO.find(m => m.code === document.header?.metodoPago)?.name || 'Efectivo';
        const sucursalBranch = branches.find(b => b.codigo_establecimiento === document.header?.sucursal);
        const sucursalNombre = sucursalBranch ? `${sucursalBranch.branch_name} (${sucursalBranch.codigo_establecimiento})` : 'Matriz';

        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}>
              Resumen del Documento
            </Typography>

            {/* Información del Header */}
            <Card sx={{ mb: 3, mx: isMobile ? 0.5 : 0 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Datos del Documento
                </Typography>
                <Grid container spacing={isMobile ? 1 : 2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                      Fecha de Emisión
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '1rem' }}>
                      {document.header?.fechaEmision || new Date().toISOString().split('T')[0]}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                      Forma de Pago
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '1rem' }}>
                      {formaPagoNombre}
                    </Typography>
                  </Grid>
                  {document.header?.formaPago === 'credito' && (
                    <Grid item xs={6} sm={3}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                        Plazo
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '1rem' }}>
                        {document.header?.cantidadPlazo || 30} {document.header?.plazo || 'días'}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={6} sm={3}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                      Método de Pago
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '1rem' }}>
                      {metodoPagoNombre}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                      Sucursal
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '1rem' }}>
                      {sucursalNombre}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3, mx: isMobile ? 0.5 : 0 }}>
              <CardContent>
                <Grid container spacing={isMobile ? 1 : 2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                      Tipo de Documento
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '1rem' }}>
                      {DTE_TYPES.find((d) => d.code === document.identificacion.tipoDte)?.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                      Fecha de Emisión
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '1rem' }}>
                      {document.identificacion.fecEmi || new Date().toISOString().split('T')[0]}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={4} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.875rem' }}>
                      Total Gravado
                    </Typography>
                    <Typography variant={isMobile ? 'body2' : 'h6'} sx={{ fontSize: isMobile ? '0.8rem' : '1.25rem' }}>${totalGravada.toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={4} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.875rem' }}>
                      IVA (13%)
                    </Typography>
                    <Typography variant={isMobile ? 'body2' : 'h6'} sx={{ fontSize: isMobile ? '0.8rem' : '1.25rem' }}>${totalIVA.toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={4} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.875rem' }}>
                      Total
                    </Typography>
                    <Typography variant={isMobile ? 'body2' : 'h5'} color="primary" sx={{ fontSize: isMobile ? '0.9rem' : '1.5rem' }}>${total.toFixed(2)}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {submitResult && (
              <Alert
                severity={wasAcceptedByMh || submitResult.success ? 'success' : 'error'}
                sx={{ mb: 2 }}
              >
                {submitResult.message}
                {submitResult.codigoGeneracion && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Código de Generación:</strong> {submitResult.codigoGeneracion}
                  </Typography>
                )}
                {submitResult.selloRecepcion && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Sello de Recepción:</strong> {submitResult.selloRecepcion}
                  </Typography>
                )}
                {submitResult.status && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Estado:</strong> {submitResult.status === 'accepted' ? 'Aceptado por MH' : submitResult.status}
                  </Typography>
                )}
                {/* Un documento con sello está aceptado aunque MH devuelva observaciones. */}
                {submitResult.observaciones && submitResult.observaciones.length > 0 && (
                  <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      color={wasAcceptedByMh ? 'warning.main' : 'error'}
                      gutterBottom
                    >
                      {wasAcceptedByMh
                        ? 'Observaciones o advertencias de MH:'
                        : 'Errores de validación detectados por MH:'}
                    </Typography>
                    {submitResult.clasificaMsg && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Código de clasificación:</strong> {submitResult.clasificaMsg}
                        {submitResult.codigoMsg && <span> - Código: {submitResult.codigoMsg}</span>}
                      </Typography>
                    )}
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      {submitResult.observaciones.map((obs, index) => (
                        <li key={index}>
                          <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {obs}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                )}
              </Alert>
            )}

            {schemaDiagnostics.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Diagnóstico AJV de MH: {schemaDiagnostics.length} observación(es)
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Estas observaciones se registran para la migración y no bloquean el envío en esta fase.
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {schemaDiagnostics.map((diagnostic, index) => (
                    <li key={`${diagnostic.path}-${diagnostic.keyword}-${index}`}>
                      <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        [{diagnostic.section}] {diagnostic.path}: {diagnostic.message}
                      </Typography>
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              {wasAcceptedByMh && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<HomeIcon />}
                  onClick={() => navigate('/billing', { replace: true })}
                >
                  Volver al inicio
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                startIcon={<Send />}
                onClick={handleSignAndSend}
                disabled={isSubmitting || wasAcceptedByMh}
              >
                {isSubmitting ? 'Firmando y Enviando a MH...' : 'Firmar y Enviar a MH'}
              </Button>
            </Box>
          </Box>
        );
      }
        return null;
    }
  };

  return (
    <Box sx={{ 
      ml: isMobile ? 0 : 0, 
      mr: isMobile ? 0 : 0,
      width: '100%', 
      p: 0,
      px: 0,
      overflowX: 'hidden',
      '& *': {
        maxWidth: '100%',
      }
    }}>
      <Typography variant="h5" gutterBottom sx={{ fontSize: isMobile ? '1.25rem' : '1.5rem' }}>
        Nuevo DTE - {DTE_TYPES.find((d) => d.code === document.identificacion.tipoDte)?.name || 'Seleccione tipo de documento'}
      </Typography>

      <Stepper 
        activeStep={activeStep} 
        alternativeLabel={!isMobile}
        orientation={isMobile ? 'horizontal' : 'horizontal'}
        sx={{ 
          mb: 2,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.paper',
          pb: 1,
          minWidth: '100%',
          '& .MuiStepIcon-root': {
            fontSize: isMobile ? '1.2rem' : '2rem',
          },
          '& .MuiStepLabel-label': {
            fontSize: isMobile ? '0rem' : '0.875rem',
            display: isMobile ? 'none' : 'block',
          },
          '& .MuiStepIcon-root + .MuiStepLabel-label': {
            mt: isMobile ? 0 : 0,
          }
        }}
      >
        {[
          { label: 'Tipo', icon: <ReceiptLong sx={{ fontSize: isMobile ? 14 : 24 }} /> },
          { label: 'Receptor', icon: <Person sx={{ fontSize: isMobile ? 14 : 24 }} /> },
          { label: 'Detalle', icon: <Assignment sx={{ fontSize: isMobile ? 14 : 24 }} /> },
          { label: 'Resumen', icon: <CheckCircle sx={{ fontSize: isMobile ? 14 : 24 }} /> }
        ].map((step, index) => (
          <Step key={step.label}>
            <StepLabel
              StepIconComponent={() => (
                <Box
                  sx={{
                    width: isMobile ? 28 : 40,
                    height: isMobile ? 28 : 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: index <= activeStep ? 'primary.main' : 'grey.300',
                    color: index <= activeStep ? 'white' : 'grey.500',
                  }}
                >
                  {step.icon}
                </Box>
              )}
            >
              {!isMobile && step.label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card>
        <CardContent sx={{ p: isMobile ? 1 : 2 }}>
          {renderStepContent()}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBack}
              disabled={activeStep === 0}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
            >
              Atrás
            </Button>

            {activeStep < steps.length - 1 && (
              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                onClick={handleNext}
                size={isMobile ? 'small' : 'medium'}
                fullWidth={isMobile}
              >
                Siguiente
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DTEWizard;
